<template>
  <div class="aord">
    <section class="sui-card sui-card--flush aord__board" data-tour="admin-orders-table">
      <nav class="aord__filters" aria-label="주문 필터">
        <div class="aord__filter-group" data-tour="admin-orders-status" role="group" aria-label="주문 상태">
          <span class="aord__filter-group-label">주문 상태</span>
          <button
            v-for="tab in statusTabs"
            :key="tab.key || 'all'"
            type="button"
            class="aord__filter"
            :class="[
              tab.tone ? `aord__filter--tone-${tab.tone}` : '',
              { 'is-active': !taxOnly && !work && status === tab.key },
            ]"
            @click="setStatus(tab.key)"
          >
            <span class="aord__filter-label">{{ tab.label }}</span>
            <span class="aord__filter-count">{{ tab.count }}</span>
          </button>
        </div>
        <div class="aord__filter-group" data-tour="admin-orders-work" role="group" aria-label="처리할 일">
          <span class="aord__filter-group-label">처리할 일</span>
          <button
            v-for="tab in workTabs"
            :key="tab.key"
            type="button"
            class="aord__filter"
            :class="[
              tab.tone ? `aord__filter--tone-${tab.tone}` : '',
              { 'is-active': !taxOnly && work === tab.key },
            ]"
            @click="setWork(tab.key)"
          >
            <span class="aord__filter-label">{{ tab.label }}</span>
            <span class="aord__filter-count">{{ tab.count }}</span>
          </button>
          <button
            type="button"
            class="aord__filter aord__filter--tax"
            :class="{ 'is-active': taxOnly }"
            data-tour="admin-orders-tax"
            @click="setTaxOnly(true)"
          >
            <span class="aord__filter-label">세금계산서</span>
            <span class="aord__filter-count">{{ taxPendingCount }}</span>
          </button>
        </div>
        <span class="aord__filters-spacer" />
        <router-link
          to="/admin/settings"
          class="aord__account-mini"
          data-tour="admin-orders-account"
          title="입금 계좌 설정으로 이동"
        >
          <AppIcon icon="mdi-bank-outline" :size="16" />
          <span class="aord__account-mini-body">
            <strong>{{ payment?.bankName || '입금 계좌 미등록' }} {{ payment?.account || '' }}</strong>
            <em>{{ payment?.holder || '계좌를 등록해 주세요' }}</em>
          </span>
        </router-link>
      </nav>

      <div class="sui-toolbar aord__toolbar" data-tour="admin-orders-toolbar">
        <div class="sui-presets" role="group" aria-label="기간">
          <button
            v-for="item in presets"
            :key="item.key"
            type="button"
            :class="{ 'is-active': preset === item.key }"
            @click="onPreset(item.key)"
          >
            {{ item.label }}
          </button>
        </div>
        <label class="sui-date">
          <span>시작일</span>
          <input v-model="fromDate" type="date" @change="onDateChange" />
        </label>
        <label class="sui-date">
          <span>종료일</span>
          <input v-model="toDate" type="date" @change="onDateChange" />
        </label>
        <label class="sui-search">
          <AppIcon icon="mdi-magnify" :size="17" />
          <input
            v-model.trim="keyword"
            type="search"
            placeholder="주문번호 · 입금자명 · 회원명 검색"
            @keydown.enter="reload"
          />
        </label>
        <button type="button" class="sui-btn" @click="reload">검색</button>
        <span class="sui-toolbar__spacer" />
        <button type="button" class="sui-btn" :disabled="!displayOrders.length" @click="downloadCsv">
          <AppIcon icon="mdi-download" :size="16" />
          CSV
        </button>
        <button type="button" class="sui-btn" :disabled="loading" @click="reload">
          <AppIcon icon="mdi-refresh" :size="16" />
          새로고침
        </button>
      </div>

      <p v-if="errorMessage" class="aord__error">
        <AppIcon icon="mdi-alert-outline" :size="16" />
        {{ errorMessage }}
      </p>

      <p v-if="taxOnly && displayOrders.length" class="aord__tax-hint">
        「발행 요청」을 누르면 수신 이메일·사업자등록증을 확인하고 발행 완료 처리할 수 있습니다.
      </p>

      <div v-if="displayOrders.length" class="sui-table-wrap">
        <table v-if="taxOnly" class="sui-table aord__table--tax">
          <thead>
            <tr>
              <th>주문번호 · 일시</th>
              <th>회원 · 입금자</th>
              <th class="is-num">입금액</th>
              <th class="is-center">세금계산서</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="order in displayOrders"
              :key="order.code"
              :class="{ 'is-focus': keywordFocusCode === order.code }"
            >
              <td>
                <span class="sui-table__strong">{{ order.code }}</span>
                <span class="sui-table__sub">{{ formatDateTime(order.orderedAt) }}</span>
                <span v-if="keywordFocusCode === order.code" class="aord__focus-tag">검색된 주문</span>
              </td>
              <td>
                <button
                  v-if="order.memberCode && order.orgName"
                  type="button"
                  class="aord__org-btn"
                  @click="openOrgDetail(order.memberCode, order.orgName, order.code)"
                >
                  {{ order.orgName }}
                </button>
                <span v-else>{{ order.orgName || '-' }}</span>
                <span class="sui-table__sub">{{ order.depositor || order.receiver }}</span>
              </td>
              <td class="is-num">
                <span class="sui-table__strong aord__amount">{{ formatWon(order.totalAmount) }}원</span>
              </td>
              <td class="is-center">
                <button
                  type="button"
                  class="sui-badge aord__tax-chip"
                  :class="taxToneOf(order)"
                  data-tour="admin-orders-tax-open"
                  @click="openTaxModal(order)"
                >
                  {{ taxLabelOf(order) }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        <div v-else class="aord__sections">
          <section
            v-for="section in orderSections"
            :key="section.key"
            class="aord__section"
            :class="[`is-${section.key}`, `aord__section--tone-${section.tone}`]"
          >
            <header class="aord__section-head">
              <span class="aord__section-icon" aria-hidden="true">
                <AppIcon :icon="section.icon" :size="20" />
              </span>
              <div class="aord__section-title">
                <h2>
                  {{ section.title }}
                  <span class="aord__section-count">{{ section.orders.length }}</span>
                </h2>
                <p>{{ section.desc }}</p>
              </div>
            </header>

            <ul v-if="section.orders.length" class="aord__cards">
              <li
                v-for="order in section.orders"
                :key="order.code"
                class="aord__card"
                :class="{
                  'is-focus': keywordFocusCode === order.code,
                  'is-expanded': expandedCode === order.code,
                }"
              >
                <!-- 카드 요약 (전체 영역이 확장 토글) -->
                <div
                  class="aord__card-summary"
                  role="button"
                  tabindex="0"
                  :aria-expanded="expandedCode === order.code"
                  :aria-controls="`aord-detail-${order.code}`"
                  @click="toggleExpand(order)"
                  @keydown.enter.prevent="toggleExpand(order)"
                  @keydown.space.prevent="toggleExpand(order)"
                >
                  <!-- 헤더 바: 코드·시각·배지·스텝 dots (접힌 카드의 유일한 스텝 표시) -->
                  <header class="aord__card-head">
                    <span class="aord__card-chevron" aria-hidden="true">
                      <AppIcon
                        :icon="expandedCode === order.code ? 'mdi-chevron-down' : 'mdi-chevron-right'"
                        :size="18"
                      />
                    </span>
                    <strong class="aord__card-code">{{ order.code }}</strong>
                    <span
                      class="aord__pay-chip"
                      :class="{ 'is-card': order.payMethod === 'TOSS' }"
                    >{{ mallPayMethodLabel(order) }}</span>
                    <span class="aord__card-time">{{ formatDateTime(order.orderedAt) }}</span>
                    <span
                      v-if="keywordFocusCode === order.code"
                      class="aord__focus-tag"
                    >검색된 주문</span>
                    <span class="aord__card-head-spacer" />
                    <!-- 상태 배지 그룹 (세금계산서·자동취소·현재상태·문의·환불) -->
                    <div class="aord__card-badges">
                      <button
                        v-if="effectiveTaxStatus(order) !== 'NONE'"
                        type="button"
                        class="sui-badge aord__tax-chip aord__tax-chip--strong"
                        :class="taxToneOf(order)"
                        data-tour="admin-orders-tax-open"
                        title="세금계산서"
                        @click.stop="openTaxModal(order)"
                      >
                        <AppIcon icon="mdi-receipt-text-outline" :size="12" />
                        세금계산서 · {{ taxLabelOf(order) }}
                      </button>
                      <span
                        v-if="order.status === 'PENDING' && !order.payNotifiedAt"
                        class="sui-badge aord__expire-badge"
                        :class="
                          pendingExpireTone(order.orderedAt) === 'alert'
                            ? 'sui-badge--red'
                            : pendingExpireTone(order.orderedAt) === 'warn'
                              ? 'sui-badge--amber'
                              : 'sui-badge--gray'
                        "
                        :title="
                          order.payMethod === 'TOSS'
                            ? `결제 없이 ${PENDING_EXPIRE_DAYS}일 지나면 자동 취소`
                            : `입금 알림 없이 ${PENDING_EXPIRE_DAYS}일 지나면 자동 취소`
                        "
                      >{{ pendingExpireLabel(order.orderedAt) }}</span>
                      <span
                        v-if="hasActiveClaim(order)"
                        class="sui-badge aord__claim-chip"
                        title="카드를 펼쳐 상품행에서 승인·반려를 처리하세요"
                      >
                        <AppIcon icon="mdi-alert-circle-outline" :size="12" />
                        {{ activeClaimLabel(order) }}
                      </span>
                      <span
                        v-if="qnaOpenCountOf(order) > 0"
                        class="sui-badge aord__qna-chip"
                        title="이 주문에 미답변 상품문의가 있습니다"
                      >
                        <AppIcon icon="mdi-comment-question-outline" :size="12" />
                        문의 {{ qnaOpenCountOf(order) }}
                      </span>
                      <span
                        v-if="refundSummaryOf(order)"
                        class="sui-badge aord__refund-chip"
                        :class="refundSummaryOf(order)!.tone"
                        title="카드를 펼쳐 상품행에서 환불 완료를 처리하세요"
                      >{{ refundSummaryOf(order)!.label }}</span>
                      <span
                        v-if="isUnpaidCancel(order)"
                        class="sui-badge aord__unpaid-chip"
                        :title="
                          order.payMethod === 'TOSS'
                            ? '결제 전에 취소되어 환불이 없습니다'
                            : '입금 전에 취소되어 환불이 없습니다'
                        "
                      >{{ order.payMethod === 'TOSS' ? '결제 전 취소' : '입금 미진행' }}</span>
                      <span class="sui-badge aord__card-status" :class="progressOf(order).tone">
                        {{ progressOf(order).title }}
                      </span>
                    </div>
                    <!-- 스텝 dots (접힌 카드 진행 표시) -->
                    <ol
                      class="aord__card-dots"
                      aria-label="처리현황"
                      data-tour="admin-orders-progress"
                      :title="progressOf(order).hint"
                    >
                      <li
                        v-for="step in fulfillSteps"
                        :key="step.key"
                        :class="{
                          'is-done': stepState(order, step.key) === 'done',
                          'is-current': stepState(order, step.key) === 'current',
                          'is-stop': order.status === 'CANCELED',
                        }"
                        :title="step.label"
                      />
                    </ol>
                  </header>

                  <!-- 상품·금액 한 줄 요약 -->
                  <div class="aord__card-sum">
                    <div class="aord__card-sum-goods">
                      <strong>{{ order.itemSummary }}</strong>
                      <span>· {{ order.itemCount }}건</span>
                    </div>
                    <div class="aord__card-sum-total">
                      <span
                        v-if="order.shippingFee"
                        class="aord__card-sum-note"
                      >
                        <span>상품 {{ formatWon(order.itemAmount) }}원</span>
                        <em class="aord__card-sum-op">+</em>
                        <span>배송 {{ formatWon(order.shippingFee) }}원</span>
                        <em class="aord__card-sum-op">=</em>
                      </span>
                      <strong class="aord__amount">{{ formatWon(order.totalAmount) }}원</strong>
                    </div>
                  </div>
                </div>

                <!-- 인라인 확장 상세 -->
                <section
                  v-if="expandedCode === order.code"
                  :id="`aord-detail-${order.code}`"
                  class="aord__card-detail"
                  data-tour="admin-orders-detail"
                >
                  <div v-if="expandedLoading" class="aord__card-detail-loading">
                    <AppIcon icon="mdi-loading" :size="20" />
                    <span>주문 상세를 불러오는 중입니다…</span>
                  </div>
                  <p v-else-if="expandedError" class="aord__error">
                    <AppIcon icon="mdi-alert-outline" :size="16" />
                    {{ expandedError }}
                  </p>
                  <template v-else-if="expandedDetail && expandedDetail.code === order.code">
                    <div
                      v-if="
                        (expandedDetail.refundPendingCount || 0) + (expandedDetail.refundDoneCount || 0) > 0
                          || (expandedDetail.status === 'CANCELED' && expandedDetail.cancelReason)
                      "
                      class="aord__detail-strip"
                    >
                      <span
                        v-if="(expandedDetail.refundPendingCount || 0) + (expandedDetail.refundDoneCount || 0) > 0"
                        class="aord__detail-strip-meta"
                      >
                        환불 대기 {{ expandedDetail.refundPendingCount || 0 }}
                        · 완료 {{ expandedDetail.refundDoneCount || 0 }}
                      </span>
                      <span
                        v-if="expandedDetail.status === 'CANCELED' && expandedDetail.cancelReason"
                        class="aord__detail-strip-reason"
                      >{{ expandedDetail.cancelReason }}</span>
                    </div>

                    <!-- ① 주문 정보 요약 표 -->
                    <table class="aord__info-table">
                      <caption>주문 정보</caption>
                      <colgroup>
                        <col class="aord__info-col-label" />
                        <col />
                        <col class="aord__info-col-label" />
                        <col />
                      </colgroup>

                      <tbody class="aord__info-group">
                        <tr>
                          <th scope="row">주문 회원</th>
                          <td>
                            <button
                              v-if="expandedDetail.memberCode && expandedDetail.orgName"
                              type="button"
                              class="aord__org-btn"
                              @click="openOrgDetail(expandedDetail.memberCode, expandedDetail.orgName, expandedDetail.code)"
                            >{{ expandedDetail.orgName }}</button>
                            <span v-else>{{ expandedDetail.orgName || '-' }}</span>
                            <span v-if="expandedDetail.memberEmail" class="aord__info-sub">{{ expandedDetail.memberEmail }}</span>
                          </td>
                          <th scope="row">발주번호</th>
                          <td>
                            <span v-if="expandedDetail.poNumber">{{ expandedDetail.poNumber }}</span>
                            <span v-else class="aord__info-muted">—</span>
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">배송지</th>
                          <td colspan="3">
                            <strong>{{ expandedDetail.receiver }}</strong>
                            <span class="aord__info-sep">·</span>
                            <span>{{ expandedDetail.phone }}</span>
                            <span class="aord__info-sep">·</span>
                            <span>
                              <template v-if="expandedDetail.zipcode">[{{ expandedDetail.zipcode }}] </template>
                              {{ expandedDetail.address }} {{ expandedDetail.addressDetail }}
                            </span>
                          </td>
                        </tr>
                        <tr v-if="expandedDetail.memo">
                          <th scope="row">요청사항</th>
                          <td colspan="3">{{ expandedDetail.memo }}</td>
                        </tr>
                        <tr>
                          <th scope="row">주문 시각</th>
                          <td>{{ formatDateTime(expandedDetail.orderedAt) }}</td>
                          <th scope="row">결제수단</th>
                          <td>{{ mallPayMethodLabel(expandedDetail) }}</td>
                        </tr>
                        <tr v-if="expandedDetail.payMethod !== 'TOSS'">
                          <th scope="row">입금자</th>
                          <td colspan="3">
                            <strong>{{ expandedDetail.depositor || expandedDetail.receiver }}</strong>
                            <span
                              v-if="expandedDetail.payNotifiedAt"
                              class="aord__info-sub"
                            >「입금했어요」 {{ formatDateTime(expandedDetail.payNotifiedAt) }}</span>
                            <span v-else class="aord__info-sub">입금 알림 없음</span>
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">결제확인</th>
                          <td colspan="3">
                            <span v-if="expandedDetail.paidAt">
                              <strong>{{ formatDateTime(expandedDetail.paidAt) }}</strong>
                              <span v-if="expandedDetail.paidBy" class="aord__info-sub">· {{ expandedDetail.paidBy }}</span>
                            </span>
                            <span v-else class="aord__info-muted">아직 확인 전</span>
                          </td>
                        </tr>
                      </tbody>

                      <tbody v-if="expandedDetail.taxInvoice" class="aord__info-group">
                        <tr class="aord__info-subhead">
                          <th colspan="4" scope="colgroup">
                            <AppIcon icon="mdi-receipt-text-outline" :size="14" />
                            <span>세금계산서</span>
                            <button
                              type="button"
                              class="aord__info-more"
                              @click="openTaxModal(expandedDetail!)"
                            >관리하기</button>
                          </th>
                        </tr>
                          <tr>
                            <th scope="row">상태</th>
                            <td>
                              <button
                                type="button"
                                class="sui-badge aord__tax-chip"
                                :class="taxToneOf(expandedDetail)"
                                @click="openTaxModal(expandedDetail!)"
                              >{{ taxStatusLabel(expandedDetail.taxStatus) }}</button>
                              <span
                                v-if="effectiveTaxStatus(expandedDetail) === 'DONE' && expandedDetail.taxAt"
                                class="aord__info-sub"
                              >발행 {{ formatDateTime(expandedDetail.taxAt) }}</span>
                            </td>
                            <th scope="row">발행 문서</th>
                            <td>
                              <a
                                v-if="expandedDetail.taxDocUrl"
                                class="aord__info-link"
                                :href="taxDocHref(expandedDetail.taxDocUrl)"
                                target="_blank"
                                rel="noopener"
                              >
                                <AppIcon icon="mdi-file-document-outline" :size="14" />
                                세금계산서 열기
                              </a>
                              <span v-else class="aord__info-muted">발행 후 링크 표시</span>
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">상호</th>
                            <td>
                              <span v-if="expandedDetail.bizName">{{ expandedDetail.bizName }}</span>
                              <span v-else class="aord__info-muted">-</span>
                            </td>
                            <th scope="row">사업자번호</th>
                            <td>
                              <span v-if="expandedDetail.bizNum">{{ formatBizNumber(expandedDetail.bizNum) }}</span>
                              <span v-else class="aord__info-muted">-</span>
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">수신 이메일</th>
                            <td>
                              <a
                                v-if="taxDisplayEmail(expandedDetail)"
                                class="aord__info-link"
                                :href="`mailto:${taxDisplayEmail(expandedDetail)}`"
                              >{{ taxDisplayEmail(expandedDetail) }}</a>
                              <span v-else class="aord__info-muted">-</span>
                              <span
                                v-if="!expandedDetail.taxEmail && expandedDetail.memberEmail"
                                class="aord__info-sub"
                              >회원 프로필</span>
                            </td>
                            <th scope="row">사업자등록증</th>
                            <td>
                              <a
                                v-if="taxDisplayBizCert(expandedDetail)"
                                class="aord__info-link"
                                :href="bizCertHref(taxDisplayBizCert(expandedDetail))"
                                target="_blank"
                                rel="noopener"
                              >
                                <AppIcon icon="mdi-file-image-outline" :size="14" />
                                등록증 열기
                              </a>
                              <span v-else class="aord__info-muted">미등록</span>
                              <span
                                v-if="!expandedDetail.bizCert && expandedDetail.memberBizCert"
                                class="aord__info-sub"
                              >회원 프로필</span>
                            </td>
                          </tr>
                          <tr v-if="expandedDetail.taxRejectReason">
                            <th scope="row">반려 사유</th>
                            <td colspan="3" class="aord__info-warn">
                              {{ expandedDetail.taxRejectReason }}
                            </td>
                          </tr>
                      </tbody>

                      <tbody
                        v-if="openQnasOf(expandedDetail).length"
                        class="aord__info-group"
                      >
                        <tr class="aord__info-subhead">
                          <th colspan="4" scope="colgroup">
                            <AppIcon icon="mdi-comment-question-outline" :size="14" />
                            <span>상품 문의</span>
                            <em class="aord__info-more-note">
                              미답변 {{ openQnasOf(expandedDetail).length }}
                            </em>
                          </th>
                        </tr>
                        <tr v-for="qna in openQnasOf(expandedDetail)" :key="qna.idx">
                          <th scope="row">{{ qna.productName || '상품' }}</th>
                          <td colspan="3">
                            <div class="aord__qna-body">
                              <span>{{ qna.question }}</span>
                              <router-link
                                class="aord__info-link"
                                :to="{
                                  path: '/admin/cs',
                                  query: { tab: 'qna', unanswered: '1', keyword: expandedDetail.code },
                                }"
                              >
                                문의 화면에서 보기
                              </router-link>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    <!-- ② 판매업체별 상품 표 -->
                    <table class="aord__items-table">
                      <caption>주문 상품</caption>
                      <thead>
                        <tr>
                          <th scope="col" class="is-item">상품</th>
                          <th scope="col" class="is-num">단가</th>
                          <th scope="col" class="is-num is-qty">수량</th>
                          <th scope="col" class="is-num">금액</th>
                          <th scope="col" class="is-status">상태 · 배송</th>
                        </tr>
                      </thead>
                      <tbody v-for="group in expandedSellerGroups" :key="group.storeName">
                        <tr class="aord__items-store">
                          <th colspan="5" scope="colgroup">
                            <span class="aord__items-store-name">
                              <AppIcon icon="mdi-storefront-outline" :size="16" />
                              <strong>{{ group.storeName }}</strong>
                            </span>
                            <em>{{ group.items.length }}건 · {{ formatWon(group.amount) }}원</em>
                          </th>
                        </tr>
                        <tr
                          v-for="item in group.items"
                          :key="item.idx"
                          class="aord__items-row"
                          :class="{
                            'is-claim-pending': isClaimPending(item),
                            'is-refund-pending': item.refundStatus === 'PENDING',
                          }"
                        >
                          <td class="is-item">
                            <strong>{{ item.name }}</strong>
                            <span v-if="item.option" class="aord__items-option">{{ item.option }}</span>
                          </td>
                          <td class="is-num">{{ formatWon(item.price) }}원</td>
                          <td class="is-num is-qty">{{ item.qty }}</td>
                          <td class="is-num is-strong">{{ formatWon(item.amount) }}원</td>
                          <td class="is-status">
                            <div class="aord__items-status">
                              <span
                                class="sui-badge"
                                :class="[itemTone(item.status), isClaimPending(item) ? 'aord__items-claim-pill' : '']"
                              >
                                <AppIcon
                                  v-if="isClaimPending(item)"
                                  icon="mdi-alert-circle-outline"
                                  :size="12"
                                />
                                {{ itemStatusLabel(item.status) }}
                              </span>
                              <span
                                v-if="isClaimPending(item) && item.claimAt"
                                class="aord__items-claim-sla"
                                :class="claimSlaTone(item)"
                                :title="`${formatDateTime(item.claimAt)} 요청`"
                              >
                                {{ claimSlaLabel(item) }}
                              </span>
                              <span
                                v-else-if="isClaimRejected(item)"
                                class="sui-badge sui-badge--gray aord__items-claim-rejected"
                                :title="item.claimResolvedAt ? `${formatDateTime(item.claimResolvedAt)} 반려` : ''"
                              >
                                <AppIcon icon="mdi-close-circle-outline" :size="12" />
                                클레임 반려
                              </span>
                              <span
                                v-if="item.refundStatus === 'DONE'"
                                class="sui-badge sui-badge--green"
                                :title="item.refundAt ? `${formatDateTime(item.refundAt)} 환불 완료` : '환불 완료'"
                              >
                                <AppIcon icon="mdi-check-circle-outline" :size="12" />
                                환불 완료
                              </span>
                              <div v-if="hasItemPrimaryActions(item)" class="aord__items-actions">
                                <template v-if="isClaimPending(item)">
                                  <button
                                    type="button"
                                    class="sui-btn sui-btn--sm sui-btn--primary aord__items-claim-approve"
                                    data-tour="admin-orders-claim-approve"
                                    :disabled="claimBusy === item.idx"
                                    @click="approveClaim(item)"
                                  >
                                    <AppIcon icon="mdi-check-circle-outline" :size="16" />
                                    승인
                                  </button>
                                  <button
                                    type="button"
                                    class="sui-btn sui-btn--sm aord__items-claim-reject"
                                    data-tour="admin-orders-claim-reject"
                                    :disabled="claimBusy === item.idx"
                                    @click="openClaimReject(item)"
                                  >
                                    <AppIcon icon="mdi-close-circle-outline" :size="16" />
                                    반려
                                  </button>
                                </template>
                                <button
                                  v-if="item.refundStatus === 'PENDING'"
                                  type="button"
                                  class="sui-btn sui-btn--sm aord__items-refund-btn"
                                  data-tour="admin-orders-refund-done"
                                  title="환불 송금 완료로 표시"
                                  @click="openRefundDone(item)"
                                >
                                  <AppIcon icon="mdi-cash-refund" :size="16" />
                                  환불 완료 처리 · {{ formatWon(item.refundAmount || item.amount) }}원
                                </button>
                                <button
                                  v-if="canAdminAdjust(item) && !hasItemShipmentUi(item)"
                                  type="button"
                                  class="sui-btn sui-btn--sm aord__items-adjust-btn"
                                  :class="{ 'is-quiet': isItemActionBusy(item) }"
                                  data-tour="admin-orders-adjust"
                                  title="전화 환불·배송 상태를 직접 바꿉니다"
                                  @click="openAdjust(item)"
                                >
                                  <AppIcon icon="mdi-tune-variant" :size="16" />
                                  조정
                                </button>
                              </div>
                              <MallShipmentLedger
                                v-if="hasItemShipmentUi(item)"
                                :item="item"
                                :show-reship="canExtraShip(item)"
                                reship-tour="admin-orders-extra-ship"
                                lookup-tour="admin-orders-tracking"
                                @track="openItemShipment(item, $event)"
                                @reship="openExtraShip(item)"
                              >
                                <template #actions>
                                  <button
                                    v-if="canAdminAdjust(item)"
                                    type="button"
                                    class="sui-btn sui-btn--sm aord__items-adjust-btn is-quiet"
                                    data-tour="admin-orders-adjust"
                                    title="전화 환불·배송 상태를 직접 바꿉니다"
                                    @click="openAdjust(item)"
                                  >
                                    조정
                                  </button>
                                </template>
                              </MallShipmentLedger>
                            </div>
                            <!-- 클레임 사유 (대기·반려) -->
                            <div v-if="item.claimReason && (isClaimPending(item) || isClaimRejected(item))" class="aord__items-claim-reason">
                              <AppIcon icon="mdi-message-text-outline" :size="13" />
                              <span>
                                <em>{{ claimRequestLabel(item) }} 사유</em>
                                {{ item.claimReason }}
                              </span>
                            </div>
                            <div v-if="isClaimRejected(item) && item.claimRejectReason" class="aord__items-claim-reason is-reject">
                              <AppIcon icon="mdi-alert-outline" :size="13" />
                              <span>
                                <em>반려 사유</em>
                                {{ item.claimRejectReason }}
                              </span>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    <!-- ③ 합계 · 메모 (2단 정렬) -->
                    <div class="aord__detail-foot">
                      <label class="aord__memo">
                        <span>내부 메모 <em>(구매자에게 보이지 않음)</em></span>
                        <textarea
                          v-model.trim="adminMemoDraft"
                          rows="2"
                          maxlength="500"
                          placeholder="입금 확인 특이사항, 연락 기록 등을 남겨 두세요."
                        />
                        <button
                          type="button"
                          class="sui-btn sui-btn--sm"
                          :disabled="memoBusy || adminMemoDraft === (expandedDetail.adminMemo || '')"
                          @click="saveMemo"
                        >
                          {{ memoBusy ? '저장 중…' : '메모 저장' }}
                        </button>
                      </label>

                      <table class="aord__totals-table">
                        <caption>결제 합계</caption>
                        <tbody>
                          <tr>
                            <th scope="row">상품 합계</th>
                            <td>{{ formatWon(expandedDetail.itemAmount) }}원</td>
                          </tr>
                          <tr>
                            <th scope="row">배송비</th>
                            <td>{{ formatWon(expandedDetail.shippingFee) }}원</td>
                          </tr>
                          <tr>
                            <th scope="row">함우리 마진 합계</th>
                            <td class="aord__platform">{{ formatWon(expandedPlatformMargin) }}원</td>
                          </tr>
                          <tr :class="{ 'is-total': !expandedRefund.refunded }">
                            <th scope="row">{{ expandedDetail.payMethod === 'TOSS' ? '결제금액' : '입금액' }}</th>
                            <td>{{ formatWon(expandedDetail.totalAmount) }}원</td>
                          </tr>
                          <tr v-if="expandedRefund.refunded">
                            <th scope="row">환불 완료</th>
                            <td>-{{ formatWon(expandedRefund.refunded) }}원</td>
                          </tr>
                          <tr v-if="expandedRefund.refundPending">
                            <th scope="row">환불 대기</th>
                            <td>{{ formatWon(expandedRefund.refundPending) }}원</td>
                          </tr>
                          <tr v-if="expandedRefund.refunded" class="is-total">
                            <th scope="row">결제 잔액</th>
                            <td>{{ formatWon(expandedRefund.balance) }}원</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <!-- ④ 힌트 + 액션 (접힌 카드에서 옮겨진 처리 CTA) -->
                    <footer class="aord__detail-actions">
                      <p class="aord__card-hint">
                        <AppIcon icon="mdi-information-outline" :size="15" />
                        <span>{{ progressOf(expandedDetail).hint }}</span>
                      </p>
                      <div class="aord__card-buttons">
                        <button
                          v-if="expandedDetail.status === 'PENDING' && expandedDetail.payMethod !== 'TOSS'"
                          type="button"
                          class="sui-btn sui-btn--sm sui-btn--primary"
                          :disabled="working === expandedDetail.code"
                          @click.stop="onConfirm(expandedDetail)"
                        >
                          결제확인
                        </button>
                        <button
                          v-if="canUnconfirm(expandedDetail)"
                          type="button"
                          class="sui-btn sui-btn--sm"
                          data-tour="admin-orders-unconfirm"
                          title="결제확인만 취소합니다. 주문은 입금 대기로 남습니다."
                          :disabled="working === expandedDetail.code"
                          @click.stop="onUnconfirm(expandedDetail)"
                        >
                          결제확인 취소
                        </button>
                        <button
                          v-if="trackItemsOf(expandedDetail).length"
                          type="button"
                          class="sui-btn sui-btn--sm"
                          data-tour="admin-orders-tracking"
                          @click.stop="openTrackingFromOrder(expandedDetail)"
                        >
                          배송조회
                        </button>
                        <button
                          v-if="expandedDetail.status !== 'CANCELED' && canCancelOrder(expandedDetail)"
                          type="button"
                          class="sui-btn sui-btn--sm sui-btn--ghost"
                          data-tour="admin-orders-cancel"
                          title="주문을 끝냅니다. 다시 살릴 수 없습니다."
                          @click.stop="onCancel(expandedDetail)"
                        >
                          주문 취소
                        </button>
                      </div>
                    </footer>
                  </template>
                </section>
              </li>
            </ul>

            <p v-else class="aord__section-empty">해당 주문이 없습니다.</p>
          </section>
        </div>
      </div>

      <div v-else class="sui-empty">
        <AppIcon icon="mdi-receipt-text-outline" :size="32" />
        <p v-if="loading">불러오는 중입니다…</p>
        <template v-else-if="keyword.trim()">
          <p class="aord__empty-title">
            <strong>「{{ keyword.trim() }}」</strong>에 해당하는 주문을 찾지 못했습니다.
          </p>
          <p class="aord__empty-sub">
            주문번호 · 입금자명 · 회원명 어느 하나에도 일치하지 않습니다.
            취소·삭제된 주문일 수 있어요.
          </p>
          <button type="button" class="sui-btn is-primary" @click="clearKeyword">
            검색 지우고 전체 보기
          </button>
        </template>
        <p v-else>조건에 맞는 주문이 없습니다.</p>
      </div>
    </section>

    <div v-if="cancelTarget" class="aord__modal" @click.self="closeCancel">
      <form class="aord__cancel-dialog" @submit.prevent="submitCancel">
        <h3>주문 취소</h3>
        <p class="aord__compare">
          이 주문을 끝냅니다. 다시 살릴 수 없습니다.
          <template v-if="cancelTarget.status === 'PAID' && cancelTarget.payMethod === 'TOSS'">
            결제된 카드·간편결제 금액이 결제 수단으로 환불됩니다.
          </template>
          <template v-else-if="cancelTarget.status === 'PAID'">
            입금 확인만 잘못 눌렀다면 닫고 <strong>결제확인 취소</strong>를 누르세요. 그러면 주문은 입금 대기로 남습니다.
          </template>
        </p>
        <p class="aord__cancel-sub">
          {{ cancelTarget.code }} · 주문금액 {{ formatWon(cancelTarget.totalAmount) }}원
          <span class="aord__cancel-hint">
            (상품 {{ formatWon(cancelTarget.itemAmount) }}원
            <template v-if="cancelTarget.shippingFee">
              · 배송 {{ formatWon(cancelTarget.shippingFee) }}원 — 전량 취소 시 포함
            </template>
            )
          </span>
        </p>
        <label class="sui-field">
          <span>취소 사유</span>
          <textarea
            v-model.trim="cancelReason"
            class="sui-input"
            rows="4"
            maxlength="255"
            placeholder="구매 기관·판매업체에 안내할 취소 사유를 적어 주세요."
          />
        </label>
        <div class="aord__panel-actions">
          <button type="button" class="sui-btn" @click="closeCancel">닫기</button>
          <button
            type="submit"
            class="sui-btn sui-btn--primary"
            :disabled="!cancelReason || working === cancelTarget.code"
          >
            취소 확정
          </button>
        </div>
      </form>
    </div>

    <!-- 추가발송 등록 -->
    <div v-if="extraShipTarget" class="aord__modal" @click.self="extraShipTarget = null">
      <form class="aord__extra-dialog" @submit.prevent="submitExtraShip">
        <header>
          <h3>
            <AppIcon icon="mdi-truck-plus-outline" :size="18" />
            재발송 등록
          </h3>
          <button
            type="button"
            class="aord__close"
            aria-label="닫기"
            @click="extraShipTarget = null"
          >
            <AppIcon icon="mdi-close" :size="18" />
          </button>
        </header>
        <p class="aord__extra-sub">
          <strong>{{ extraShipTarget.orderCode || expandedDetail?.code }}</strong>
          · {{ extraShipTarget.storeName }} · {{ extraShipTarget.name }}
        </p>
        <p class="aord__extra-note">
          주문번호는 바뀌지 않습니다. 오발송·누락으로 택배를 한 번 더 보냈을 때, 본발송과 다른 송장만
          추가합니다. 카톤 단위는 주문 수량일 뿐 배송을 나누지 않습니다.
        </p>

        <label class="sui-field">
          <span>택배사</span>
          <select v-model="extraShipForm.courier" class="sui-select">
            <option v-for="name in COURIERS" :key="name" :value="name">{{ name }}</option>
          </select>
        </label>

        <label class="sui-field">
          <span>
            재발송 송장번호{{
              isDirectCourierName(extraShipForm.courier) ? ' (직접배송은 생략 가능)' : ''
            }}
          </span>
          <input
            v-model.trim="extraShipForm.invoice"
            class="sui-input"
            placeholder="숫자만 입력"
          />
        </label>

        <label class="sui-field">
          <span>이번에 보내는 수량</span>
          <input
            v-model.number="extraShipForm.qty"
            class="sui-input"
            type="number"
            min="1"
            :max="extraShipTarget.qty"
          />
        </label>

        <label class="sui-field">
          <span>사유</span>
          <input
            v-model.trim="extraShipForm.memo"
            class="sui-input"
            maxlength="120"
            placeholder="예: 오발송 보충"
          />
        </label>

        <div class="aord__extra-actions">
          <button type="button" class="sui-btn" @click="extraShipTarget = null">취소</button>
          <button
            type="submit"
            class="sui-btn sui-btn--primary"
            :disabled="extraShipBusy"
          >
            {{ extraShipBusy ? '등록 중…' : '재발송 등록' }}
          </button>
        </div>
      </form>
    </div>

    <!-- 클레임 반려 -->
    <div v-if="claimRejectTarget" class="aord__modal" @click.self="closeClaimReject">
      <form class="aord__extra-dialog" @submit.prevent="submitClaimReject">
        <header>
          <h3>
            <AppIcon icon="mdi-close-circle-outline" :size="18" />
            {{ claimRequestLabel(claimRejectTarget) }} 반려
          </h3>
          <button type="button" class="aord__close" aria-label="닫기" @click="closeClaimReject">
            <AppIcon icon="mdi-close" :size="18" />
          </button>
        </header>
        <p class="aord__extra-sub">
          <strong>{{ expandedDetail?.code }}</strong>
          · {{ claimRejectTarget.storeName }} · {{ claimRejectTarget.name }} · {{ claimRejectTarget.qty }}개
        </p>
        <p v-if="claimRejectTarget.claimReason" class="aord__extra-note">
          <em>구매 기관 사유</em>
          {{ claimRejectTarget.claimReason }}
        </p>

        <label class="sui-field">
          <span>반려 사유 <em>(구매자에게 안내됩니다)</em></span>
          <textarea
            v-model.trim="claimRejectReason"
            class="sui-input"
            rows="4"
            maxlength="255"
            placeholder="예: 배송이 이미 시작되어 취소가 어렵습니다."
          />
        </label>

        <div class="aord__extra-actions">
          <button type="button" class="sui-btn" @click="closeClaimReject">닫기</button>
          <button
            type="submit"
            class="sui-btn sui-btn--danger"
            :disabled="!claimRejectReason || claimBusy === claimRejectTarget.idx"
          >
            {{ claimBusy === claimRejectTarget.idx ? '처리 중…' : '반려 확정' }}
          </button>
        </div>
      </form>
    </div>

    <!-- 관리자 임의 조정 (전화 환불 · 배송 상태) -->
    <div v-if="adjustTarget" class="aord__modal" @click.self="closeAdjust">
      <form class="aord__refund-dialog aord__adjust-dialog" @submit.prevent="submitAdjust">
        <header>
          <h3>
            <AppIcon icon="mdi-tune-variant" :size="18" />
            주문 조정
          </h3>
          <button type="button" class="aord__close" aria-label="닫기" @click="closeAdjust">
            <AppIcon icon="mdi-close" :size="18" />
          </button>
        </header>

        <div class="aord__refund-summary">
          <p>
            <strong>{{ expandedDetail?.code }}</strong>
            <em>{{ adjustTarget.storeName }} · {{ adjustTarget.name }} · {{ adjustTarget.qty }}개</em>
            <em>지금 {{ itemStatusLabel(adjustTarget.status) }}</em>
          </p>
          <strong class="aord__refund-summary-amount">
            {{ formatWon(adjustTarget.refundAmount || adjustTarget.amount) }}원
          </strong>
        </div>

        <p class="aord__extra-note">
          구매자가 화면에서 요청하지 않아도 전화·CS로 처리한 내용을 여기에 반영합니다.
          환불 완료는 되돌릴 수 없습니다.
        </p>

        <fieldset class="aord__adjust-choices">
          <legend>무엇을 할까요</legend>
          <label>
            <input v-model="adjustForm.action" type="radio" value="refund-done" />
            <span>
              <strong>{{ expandedDetail?.payMethod === 'TOSS' ? '카드 결제 취소' : '환불 완료로 표시' }}</strong>
              {{
                expandedDetail?.payMethod === 'TOSS'
                  ? '상품을 취소하고 결제했던 카드·간편결제로 환불합니다.'
                  : '송금까지 끝났으면 상품을 취소하고 환불 완료로 둡니다.'
              }}
            </span>
          </label>
          <label v-if="expandedDetail?.payMethod !== 'TOSS'">
            <input v-model="adjustForm.action" type="radio" value="refund-pending" />
            <span>
              <strong>환불 대기로 두기</strong>
              상품만 취소하고, 송금은 나중에 「환불 완료 처리」로 마감합니다.
            </span>
          </label>
          <label>
            <input v-model="adjustForm.action" type="radio" value="status" />
            <span>
              <strong>배송 상태만 바꾸기</strong>
              발송 준비·배송중·배송완료·구매확정 등을 직접 맞춥니다.
            </span>
          </label>
        </fieldset>

        <label v-if="adjustForm.action === 'status'" class="aord__adjust-status">
          <span>바꿀 상태</span>
          <select v-model="adjustForm.status">
            <option
              v-for="row in ADMIN_ADJUST_STATUSES"
              :key="row.value"
              :value="row.value"
            >
              {{ row.label }}
            </option>
          </select>
        </label>

        <div v-if="adjustForm.action !== 'status' && expandedDetail?.payMethod !== 'TOSS'" class="aord__refund-fields">
          <label>
            <span>은행{{ adjustForm.action === 'refund-done' ? '' : ' (선택)' }}</span>
            <select v-model="adjustForm.bank">
              <option value="">선택</option>
              <option v-for="bank in MALL_BANKS" :key="bank" :value="bank">{{ bank }}</option>
            </select>
          </label>
          <label>
            <span>계좌번호{{ adjustForm.action === 'refund-done' ? '' : ' (선택)' }}</span>
            <input
              v-model.trim="adjustForm.account"
              type="text"
              inputmode="numeric"
              maxlength="50"
            />
          </label>
          <label class="is-full">
            <span>예금주{{ adjustForm.action === 'refund-done' ? '' : ' (선택)' }}</span>
            <input v-model.trim="adjustForm.holder" type="text" maxlength="50" />
          </label>
        </div>

        <label class="aord__adjust-reason">
          <span>사유</span>
          <textarea
            v-model.trim="adjustForm.reason"
            rows="3"
            maxlength="255"
            placeholder="예: 전화로 환불 요청 · 담당자 홍길동"
          />
        </label>

        <div class="aord__extra-actions">
          <button type="button" class="sui-btn" @click="closeAdjust">닫기</button>
          <button
            type="submit"
            class="sui-btn sui-btn--primary"
            :disabled="!canSubmitAdjust"
          >
            {{ adjustBusy ? '처리 중…' : adjustSubmitLabel }}
          </button>
        </div>
      </form>
    </div>

    <!-- 환불 완료 처리 -->
    <div v-if="refundTarget" class="aord__modal" @click.self="closeRefundDone">
      <form class="aord__refund-dialog" @submit.prevent="submitRefundDone">
        <header>
          <h3>
            <AppIcon icon="mdi-cash-refund" :size="18" />
            환불 완료 확인
          </h3>
          <button type="button" class="aord__close" aria-label="닫기" @click="closeRefundDone">
            <AppIcon icon="mdi-close" :size="18" />
          </button>
        </header>

        <div class="aord__refund-summary">
          <p>
            <strong>{{ expandedDetail?.code }}</strong>
            <em>{{ refundTarget.storeName }} · {{ refundTarget.name }} · {{ refundTarget.qty }}개</em>
            <em v-if="refundTarget.claimReason">사유 {{ refundTarget.claimReason }}</em>
            <em class="aord__refund-summary-type">{{ refundClaimLabelOf(refundTarget) }}</em>
          </p>
          <strong class="aord__refund-summary-amount">
            {{ formatWon(refundTarget.refundAmount || refundTarget.amount) }}원
          </strong>
        </div>

        <p class="aord__extra-note">
          {{
            expandedDetail?.payMethod === 'TOSS'
              ? '결제했던 카드·간편결제로 환불하고 완료로 표시합니다. 통장 송금은 없습니다.'
              : '아래 계좌로 송금했는지 확인한 뒤 완료로 표시합니다. 계좌가 비어 있으면 여기서 입력할 수 있습니다.'
          }}
        </p>

        <div v-if="expandedDetail?.payMethod !== 'TOSS'" class="aord__refund-fields">
          <label>
            <span>은행</span>
            <select v-model="refundForm.bank">
              <option value="" disabled>선택</option>
              <option v-for="bank in MALL_BANKS" :key="bank" :value="bank">{{ bank }}</option>
            </select>
          </label>
          <label>
            <span>계좌번호</span>
            <input
              v-model.trim="refundForm.account"
              type="text"
              inputmode="numeric"
              maxlength="50"
            />
          </label>
          <label>
            <span>예금주</span>
            <input v-model.trim="refundForm.holder" type="text" maxlength="50" />
          </label>
          <label class="is-full">
            <span>메모 (선택)</span>
            <input
              v-model.trim="refundForm.memo"
              type="text"
              maxlength="255"
              placeholder="송금 일시·담당자 등"
            />
          </label>
        </div>

        <div class="aord__extra-actions">
          <button type="button" class="sui-btn" @click="closeRefundDone">닫기</button>
          <button
            type="submit"
            class="sui-btn sui-btn--primary"
            :disabled="
              refundBusy ||
              (expandedDetail?.payMethod !== 'TOSS' &&
                (!refundForm.bank || !refundForm.account || !refundForm.holder))
            "
          >
            {{
              refundBusy
                ? '처리 중…'
                : expandedDetail?.payMethod === 'TOSS'
                  ? '카드 환불 완료'
                  : '송금 완료로 표시'
            }}
          </button>
        </div>
      </form>
    </div>

    <MallTrackingModal
      v-if="trackTarget"
      :item-idx="trackTarget.idx"
      :courier="trackTarget.courier"
      :invoice="trackTarget.invoice"
      :product-name="trackTarget.name"
      :order-code="trackTarget.orderCode"
      @close="onCloseTracking"
    />

    <!-- 세금계산서 처리 -->
    <div
      v-if="taxTarget"
      class="aord__modal aord__modal--tax"
      data-tour="admin-orders-tax-modal"
      @click.self="closeTaxModal"
    >
      <div class="aord__panel aord__panel--tax">
        <header class="aord__panel-head">
          <h2>
            세금계산서
            <em>{{ taxTarget.code }}</em>
          </h2>
          <span class="sui-badge" :class="taxToneOf(taxTarget)">{{ taxLabelOf(taxTarget) }}</span>
          <button type="button" class="aord__close" aria-label="닫기" @click="closeTaxModal">
            <AppIcon icon="mdi-close" :size="20" />
          </button>
        </header>

        <div class="aord__panel-body">
          <p v-if="taxLoading" class="aord__tax-modal-loading">불러오는 중…</p>

          <dl v-else class="aord__meta aord__tax-modal-meta">
            <div>
              <dt>주문번호</dt>
              <dd>{{ taxTarget.code }}</dd>
            </div>
            <div>
              <dt>발송대상 회원</dt>
              <dd>{{ taxTarget.orgName || '-' }}</dd>
            </div>
            <div>
              <dt>수령인</dt>
              <dd>{{ taxTarget.receiver || '-' }}</dd>
            </div>
            <div>
              <dt>휴대폰</dt>
              <dd>
                <a
                  v-if="taxTarget.phone"
                  class="aord__tax-phone"
                  :href="`tel:${taxTarget.phone.replace(/\D/g, '')}`"
                >{{ formatKrPhone(taxTarget.phone) }}</a>
                <span v-else class="aord__tax-missing">미입력</span>
              </dd>
            </div>
            <div>
              <dt>입금자</dt>
              <dd>{{ taxTarget.depositor || taxTarget.receiver || '-' }}</dd>
            </div>
            <div>
              <dt>입금액</dt>
              <dd>{{ formatWon(taxTarget.totalAmount) }}원</dd>
            </div>
            <div>
              <dt>상호</dt>
              <dd>{{ taxTarget.bizName || '-' }}</dd>
            </div>
            <div>
              <dt>사업자번호</dt>
              <dd>{{ taxTarget.bizNum || '-' }}</dd>
            </div>
            <div>
              <dt>수신 이메일</dt>
              <dd>
                <template v-if="taxDisplayEmail(taxTarget)">
                  <a
                    class="aord__tax-email"
                    :href="`mailto:${taxDisplayEmail(taxTarget)}`"
                  >{{ taxDisplayEmail(taxTarget) }}</a>
                  <span v-if="!taxTarget.taxEmail && taxTarget.memberEmail" class="aord__tax-note">
                    회원 프로필
                  </span>
                </template>
                <span v-else class="aord__tax-missing">미입력</span>
              </dd>
            </div>
            <div>
              <dt>사업자등록증</dt>
              <dd>
                <template v-if="taxDisplayBizCert(taxTarget)">
                  <a
                    class="aord__tax-doc"
                    :href="bizCertHref(taxDisplayBizCert(taxTarget)!)"
                    target="_blank"
                    rel="noopener"
                  >파일 보기</a>
                  <span v-if="!taxTarget.bizCert && taxTarget.memberBizCert" class="aord__tax-note">
                    회원 프로필
                  </span>
                </template>
                <span v-else class="aord__tax-missing">없음</span>
              </dd>
            </div>
            <div v-if="effectiveTaxStatus(taxTarget) === 'DONE' && taxTarget.taxAt">
              <dt>발행 완료</dt>
              <dd>{{ formatDateTime(taxTarget.taxAt) }}</dd>
            </div>
            <div v-if="taxTarget.taxStatus === 'DONE' && taxTarget.taxDocUrl">
              <dt>발행 문서</dt>
              <dd>
                <a :href="taxDocHref(taxTarget.taxDocUrl)" target="_blank" rel="noopener">세금계산서 보기</a>
              </dd>
            </div>
          </dl>

          <ul
            v-if="!taxLoading && effectiveTaxStatus(taxTarget) === 'REQUESTED' && taxIssuesOf(taxTarget).length"
            class="aord__tax-issues"
          >
            <li v-for="issue in taxIssuesOf(taxTarget)" :key="issue">{{ issue }}</li>
          </ul>

          <p
            v-if="!taxLoading && effectiveTaxStatus(taxTarget) === 'REQUESTED'"
            class="aord__tax-modal-hint"
          >
            수신 이메일과 사업자등록증을 확인한 뒤, 세금계산서를 발행했다면 「발행 완료」를 눌러 주세요.
            정보에 문제가 있으면 「발행 요청 반려」로 안내 문자를 보낼 수 있습니다.
          </p>
          <p
            v-else-if="!taxLoading && effectiveTaxStatus(taxTarget) === 'DONE'"
            class="aord__tax-modal-hint"
          >
            {{ taxTarget.taxAt ? `${formatDateTime(taxTarget.taxAt)}에 발행 완료 처리했습니다.` : '발행 완료 상태입니다.' }}
            잘못 처리했다면 「발행 완료 취소」로 되돌릴 수 있습니다.
          </p>

          <div class="aord__tax-modal-actions" data-tour="admin-orders-tax-action">
            <button
              v-if="effectiveTaxStatus(taxTarget) === 'REQUESTED'"
              type="button"
              class="sui-btn sui-btn--primary"
              data-tour="admin-orders-tax-done"
              :disabled="working === taxTarget.code || taxLoading"
              @click="onTaxComplete(taxTarget)"
            >
              {{ working === taxTarget.code ? '처리 중…' : '발행 완료' }}
            </button>
            <button
              v-if="effectiveTaxStatus(taxTarget) === 'DONE'"
              type="button"
              class="sui-btn"
              data-tour="admin-orders-tax-undo"
              :disabled="working === taxTarget.code || taxLoading"
              @click="onTaxUndo(taxTarget)"
            >
              발행 완료 취소
            </button>
            <button
              v-if="effectiveTaxStatus(taxTarget) === 'REQUESTED'"
              type="button"
              class="sui-btn sui-btn--ghost aord__tax-reject-btn"
              data-tour="admin-orders-tax-reject"
              :disabled="working === taxTarget.code || taxLoading"
              @click="openTaxReject(taxTarget)"
            >
              발행 요청 반려
            </button>
            <button type="button" class="sui-btn sui-btn--ghost" @click="openDetailFromTax">
              주문 펼치기
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 세금계산서 반려 -->
    <div
      v-if="taxRejectTarget"
      class="aord__modal aord__modal--tax-reject"
      data-tour="admin-orders-tax-reject-modal"
      @click.self="closeTaxReject"
    >
      <div class="aord__panel aord__panel--tax-reject">
        <header class="aord__panel-head">
          <h2>발행 요청 반려</h2>
          <button type="button" class="aord__close" aria-label="닫기" @click="closeTaxReject">
            <AppIcon icon="mdi-close" :size="20" />
          </button>
        </header>
        <div class="aord__panel-body">
          <p class="aord__tax-reject-lead">
            {{ taxRejectTarget.code }} · {{ taxRejectTarget.orgName || taxRejectTarget.receiver }}
          </p>

          <section class="aord__tax-reject-section">
            <h3>확인된 문제</h3>
            <ul class="aord__tax-issues">
              <li v-for="issue in taxRejectIssues" :key="issue">{{ issue }}</li>
            </ul>
            <label class="aord__tax-reject-extra">
              <span>추가 사유 (선택)</span>
              <input
                v-model.trim="taxRejectExtra"
                type="text"
                maxlength="200"
                placeholder="예: 사업자등록증 화질이 낮습니다"
              />
            </label>
          </section>

          <section class="aord__tax-reject-section">
            <h3>안내 문자</h3>
            <label class="aord__tax-reject-sms">
              <textarea v-model="taxRejectSms" rows="9" maxlength="2000" />
            </label>
            <label class="aord__tax-reject-send">
              <input v-model="taxRejectSendSms" type="checkbox" />
              <span>{{ formatKrPhone(taxRejectTarget.phone) || '휴대폰 미입력' }}로 문자 보내기</span>
            </label>
          </section>

          <div class="aord__tax-modal-actions">
            <button type="button" class="sui-btn" @click="closeTaxReject">닫기</button>
            <button
              type="button"
              class="sui-btn sui-btn--primary"
              :disabled="!taxRejectIssues.length || working === taxRejectTarget.code"
              @click="submitTaxReject"
            >
              {{
                working === taxRejectTarget.code
                  ? '처리 중…'
                  : taxRejectSendSms
                    ? '반려 · 문자 보내기'
                    : '반려하기'
              }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 회원정보 -->
    <div
      v-if="orgModalOpen"
      class="aord__modal"
      data-tour="admin-orders-org-modal"
      @click.self="closeOrgDetail"
    >
      <div class="aord__panel aord__panel--org">
        <header class="aord__panel-head">
          <h2>
            회원정보
            <em v-if="orgDetail?.orgName">{{ orgDetail.orgName }}</em>
          </h2>
          <button type="button" class="aord__close" aria-label="닫기" @click="closeOrgDetail">
            <AppIcon icon="mdi-close" :size="20" />
          </button>
        </header>

        <div class="aord__panel-body">
          <p v-if="orgLoading" class="aord__org-status">불러오는 중…</p>
          <p v-else-if="orgError" class="aord__org-status aord__org-status--error">{{ orgError }}</p>
          <template v-else-if="orgDetail">
            <section class="aord__org-section">
              <h3 class="aord__org-section-title">기본 정보</h3>
              <dl class="aord__meta aord__meta--org">
                <div>
                  <dt>회원명</dt>
                  <dd>{{ orgDetail.orgName || '-' }}</dd>
                </div>
                <div>
                  <dt>장기요양기관기호</dt>
                  <dd>{{ orgDetail.longtermCode || '-' }}</dd>
                </div>
                <div>
                  <dt>로그인 아이디</dt>
                  <dd>{{ orgDetail.userId || '-' }}</dd>
                </div>
                <div>
                  <dt>로그인 방식</dt>
                  <dd>
                    {{ orgDetail.loginType === 'kakao' ? '카카오' : '비밀번호' }}
                    <template v-if="orgDetail.oauthEmail"> · {{ orgDetail.oauthEmail }}</template>
                  </dd>
                </div>
                <div>
                  <dt>대표자</dt>
                  <dd>{{ orgDetail.bossName || '-' }}</dd>
                </div>
                <div>
                  <dt>대표 연락처</dt>
                  <dd>{{ formatKrPhone(orgDetail.bossPhone) || orgDetail.bossPhone || '-' }}</dd>
                </div>
                <div>
                  <dt>전화</dt>
                  <dd>{{ formatKrPhone(orgDetail.orgPhone || '') || orgDetail.orgPhone || '-' }}</dd>
                </div>
                <div>
                  <dt>이메일</dt>
                  <dd>{{ orgDetail.bossEmail || '-' }}</dd>
                </div>
                <div class="aord__meta-span">
                  <dt>주소</dt>
                  <dd>
                    <template v-if="orgDetail.address || orgDetail.zipcode">
                      <span v-if="orgDetail.zipcode">({{ orgDetail.zipcode }}) </span>
                      {{ orgDetail.address || '' }}
                      {{ orgDetail.addressDetail || '' }}
                    </template>
                    <template v-else>-</template>
                  </dd>
                </div>
              </dl>
            </section>

            <section class="aord__org-section">
              <h3 class="aord__org-section-title">사업자 · 환불</h3>
              <dl class="aord__meta aord__meta--org">
                <div>
                  <dt>사업구분</dt>
                  <dd>{{ orgDetail.bizGubun || '-' }}</dd>
                </div>
                <div>
                  <dt>사업자번호</dt>
                  <dd>{{ orgDetail.bizNum || '-' }}</dd>
                </div>
                <div>
                  <dt>사업자등록증</dt>
                  <dd>
                    <a
                      v-if="orgDetail.bizCert"
                      class="aord__tax-doc"
                      :href="bizCertHref(orgDetail.bizCert)"
                      target="_blank"
                      rel="noopener"
                    >파일 보기</a>
                    <span v-else>-</span>
                  </dd>
                </div>
                <div class="aord__meta-span">
                  <dt>환불 계좌</dt>
                  <dd>
                    <template v-if="orgDetail.refundBank || orgDetail.refundAccount">
                      {{ orgDetail.refundBank || '' }}
                      {{ orgDetail.refundAccount || '' }}
                      <template v-if="orgDetail.refundHolder"> ({{ orgDetail.refundHolder }})</template>
                    </template>
                    <template v-else>-</template>
                  </dd>
                </div>
              </dl>
            </section>

            <section class="aord__org-section">
              <h3 class="aord__org-section-title">가입 · 인증</h3>
              <dl class="aord__meta aord__meta--org">
                <div>
                  <dt>가입 경로</dt>
                  <dd>{{ orgDetail.signupSource === 'WEB' ? '쇼핑몰' : orgDetail.signupSource || '-' }}</dd>
                </div>
                <div>
                  <dt>권한</dt>
                  <dd>{{ orgDetail.roleName || orgDetail.roleCode || '-' }}</dd>
                </div>
                <div>
                  <dt>가입일</dt>
                  <dd>{{ formatOrgDate(orgDetail.joinedAt) }}</dd>
                </div>
                <div>
                  <dt>이메일 인증</dt>
                  <dd>{{ orgDetail.emailVerified ? '완료' : '미완료' }}</dd>
                </div>
                <div>
                  <dt>휴대폰 인증</dt>
                  <dd>{{ orgDetail.phoneVerified ? '완료' : '미완료' }}</dd>
                </div>
                <div>
                  <dt>약관 동의</dt>
                  <dd>{{ orgDetail.termsAgreed ? '동의' : '미동의' }}</dd>
                </div>
                <div>
                  <dt>마케팅 동의</dt>
                  <dd>{{ orgDetail.marketingAgreed ? '동의' : '미동의' }}</dd>
                </div>
                <div>
                  <dt>주문 실적</dt>
                  <dd>
                    전체 {{ orgDetail.orderCount || 0 }}건
                    · 결제확인 {{ orgDetail.paidCount || 0 }}건
                    <template v-if="(orgDetail.orderAmount || 0) > 0">
                      · {{ formatWon(orgDetail.orderAmount || 0) }}원
                    </template>
                    <span v-if="orgDetail.lastOrderedAt" class="aord__org-sub">
                      최근 {{ formatOrgDate(orgDetail.lastOrderedAt) }}
                    </span>
                  </dd>
                </div>
              </dl>
            </section>

            <section class="aord__org-section">
              <h3 class="aord__org-section-title">
                세금계산서 요청
                <em v-if="orgTaxRequests.length">{{ orgTaxRequests.length }}건</em>
              </h3>
              <p v-if="!orgTaxRequests.length" class="aord__org-status">세금계산서 발행 요청 내역이 없습니다.</p>
              <div
                v-for="tax in orgTaxRequests"
                :key="tax.orderCode"
                class="aord__org-tax-card"
                :class="{ 'is-focus': orgFocusOrderCode === tax.orderCode }"
              >
                <header class="aord__org-tax-head">
                  <strong>{{ tax.orderCode }}</strong>
                  <span class="sui-badge" :class="memberTaxTone(tax.taxStatus)">
                    {{ taxStatusLabel(tax.taxStatus) }}
                  </span>
                </header>
                <dl class="aord__meta aord__meta--org">
                  <div>
                    <dt>주문일시</dt>
                    <dd>{{ formatDateTime(tax.orderedAt || null) }}</dd>
                  </div>
                  <div>
                    <dt>입금액</dt>
                    <dd>{{ formatWon(tax.totalAmount || 0) }}원</dd>
                  </div>
                  <div>
                    <dt>상호</dt>
                    <dd>{{ tax.bizName || '-' }}</dd>
                  </div>
                  <div>
                    <dt>사업자번호</dt>
                    <dd>{{ tax.bizNum || '-' }}</dd>
                  </div>
                  <div>
                    <dt>수신 이메일</dt>
                    <dd>
                      <a
                        v-if="tax.taxEmail || orgDetail.bossEmail"
                        class="aord__tax-email"
                        :href="`mailto:${tax.taxEmail || orgDetail.bossEmail}`"
                      >{{ tax.taxEmail || orgDetail.bossEmail }}</a>
                      <span v-else>-</span>
                      <span
                        v-if="!tax.taxEmail && orgDetail.bossEmail"
                        class="aord__tax-note"
                      >회원 프로필</span>
                    </dd>
                  </div>
                  <div>
                    <dt>사업자등록증</dt>
                    <dd>
                      <a
                        v-if="tax.bizCert || orgDetail.bizCert"
                        class="aord__tax-doc"
                        :href="bizCertHref(tax.bizCert || orgDetail.bizCert || '')"
                        target="_blank"
                        rel="noopener"
                      >파일 보기</a>
                      <span v-else>-</span>
                      <span
                        v-if="!tax.bizCert && orgDetail.bizCert"
                        class="aord__tax-note"
                      >회원 프로필</span>
                    </dd>
                  </div>
                  <div>
                    <dt>입금자</dt>
                    <dd>{{ tax.depositor || tax.receiver || '-' }}</dd>
                  </div>
                  <div>
                    <dt>수령인 · 연락처</dt>
                    <dd>
                      {{ tax.receiver || '-' }}
                      <template v-if="tax.phone">
                        · {{ formatKrPhone(tax.phone) || tax.phone }}
                      </template>
                    </dd>
                  </div>
                  <div v-if="tax.taxAt">
                    <dt>처리일시</dt>
                    <dd>{{ formatDateTime(tax.taxAt) }}</dd>
                  </div>
                  <div v-if="tax.taxDocUrl" class="aord__meta-span">
                    <dt>발행 문서</dt>
                    <dd>
                      <a
                        class="aord__tax-doc"
                        :href="taxDocHref(tax.taxDocUrl)"
                        target="_blank"
                        rel="noopener"
                      >세금계산서 보기</a>
                    </dd>
                  </div>
                  <div v-if="tax.taxRejectReason" class="aord__meta-span">
                    <dt>반려 사유</dt>
                    <dd>{{ tax.taxRejectReason }}</dd>
                  </div>
                </dl>
              </div>
            </section>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppIcon from '@/components/common/AppIcon.vue'
import MallShipmentLedger from '@/components/mall/MallShipmentLedger.vue'
import MallTrackingModal from '@/components/mall/MallTrackingModal.vue'
import { useMallTracking } from '@/composables/useMallTracking'
import { useAdminProducts } from '@/composables/useAdminProducts'
import { confirmDialog } from '@/composables/useConfirmDialog'
import { useListDateRange } from '@/composables/useListDateRange'
import { MALL_COURIERS as COURIERS, isKnownMallCourier } from '@/constants/mallCouriers'
import { formatWon } from '@/constants/sellerData'
import {
  MALL_ORDER_ITEM_STATUS_LABEL,
  MALL_ORDER_STATUS_LABEL,
  cancelOrderByAdmin,
  confirmOrderPayment,
  unconfirmOrderPayment,
  fetchAdminOrder,
  fetchAdminOrders,
  fetchPaymentInfo,
  saveAdminOrderMemo,
  saveAdminTaxStatus,
  rejectAdminTaxInvoice,
  createAdminExtraShipment,
  markAdminRefundDone,
  adjustAdminOrderItem,
  approveAdminClaim,
  rejectAdminClaim,
  mallPayMethodLabel,
  mallOrderRefundTotals,
  MALL_TAX_STATUS_LABEL,
  type AdminMallOrder,
  type MallOrderItemStatus,
  type MallOrderItemWithMargin,
  type MallPaymentInfo,
  type OrderStatusCounts,
  type AdminAdjustAction,
} from '@/api/mallOrder'
import { MALL_BANKS } from '@/constants/banks'
import {
  buildTaxRejectSms,
  listTaxInvoiceIssues,
  orderHasDelivered,
} from '@/utils/taxInvoiceIssues'
import { isExistingWaybill, type ShipmentLedgerRow } from '@/utils/mallShipment'
import { formatKrPhone } from '@/utils/formatPhone'
import { formatBizNumber } from '@/utils/bizNumber'
import type { DatePresetKey } from '@/utils/dateInput'
import {
  PENDING_EXPIRE_DAYS,
  pendingExpireLabel,
  pendingExpireTone,
} from '@/utils/mallOpsUx'
import { fetchAdminMember, type AdminMemberRow, type AdminMemberTaxRequest } from '@/api/admin'

const route = useRoute()
const router = useRouter()
const { notify } = useAdminProducts()
/*
 * 관리자 주문 화면은 건수가 많아 기본 기간이 넓을수록 첫 로딩이 눈에 띄게 늘어난다.
 * 관리자가 실제로 자주 보는 것은 최근 1개월이라, 초기 조회 부담을 낮추기 위해 기본을 1개월로 둔다.
 * 더 긴 기간이 필요하면 프리셋을 눌러 넓히면 된다.
 */
const { fromDate, toDate, preset, presets, applyPreset, onManualDateChange } = useListDateRange('1m')

/*
 * 다른 화면(문의·정산·클레임 등)에서 「관련 주문」 링크로 넘어올 때는 주문번호가 정확히 오므로
 * 기본 1개월 필터에 걸려 빈 화면이 뜨는 일을 막는다. keyword 만 있고 기간 프리셋이 명시되지
 * 않았으면 「전체」로 자동 확장한다.
 *
 * setup 최초 진입 뿐 아니라, 같은 라우트 안에서 query만 바뀌는 경우
 * (예: 이미 /admin/orders 를 열어 둔 상태에서 CS의 「관련 주문」 링크를 다시 눌러도
 *  Vue router는 컴포넌트를 재사용하므로 setup이 재실행되지 않는다) 에도
 * 아래 watch가 다시 「전체」로 넓혀 준다.
 */
function expandRangeForDeepLink() {
  const kw = String(route.query.keyword ?? '').trim()
  if (!kw) return
  if (route.query.preset) return
  // 이미 「전체」면 재적용 불필요
  if (preset.value === 'all' && !fromDate.value && !toDate.value) return
  applyPreset('all')
}
expandRangeForDeepLink()

const orders = ref<AdminMallOrder[]>([])
const counts = ref<OrderStatusCounts>({})
const payment = ref<MallPaymentInfo | null>(null)
const expandedCode = ref('')
const expandedDetail = ref<AdminMallOrder | null>(null)
const expandedLoading = ref(false)
const expandedError = ref('')
const taxTarget = ref<AdminMallOrder | null>(null)
const taxLoading = ref(false)
const taxRejectTarget = ref<AdminMallOrder | null>(null)
const taxRejectExtra = ref('')
const taxRejectSms = ref('')
const taxRejectSendSms = ref(true)

const orgModalOpen = ref(false)
const orgLoading = ref(false)
const orgError = ref('')
const orgDetail = ref<AdminMemberRow | null>(null)
const orgFocusOrderCode = ref('')
const orgTaxRequests = computed<AdminMemberTaxRequest[]>(() => orgDetail.value?.taxRequests || [])
const { trackTarget, openTracking, closeTracking } = useMallTracking()
const cancelTarget = ref<AdminMallOrder | null>(null)

/* ---- 추가발송(관리자 대리 등록) ---- */
const LAST_COURIER_KEY = 'admin-orders:lastCourier'

type ExtraShipItem = MallOrderItemWithMargin & { orderCode?: string }
const extraShipTarget = ref<ExtraShipItem | null>(null)
const extraShipForm = reactive({
  courier: loadLastCourier(),
  invoice: '',
  qty: 1,
  memo: '오발송·누락 재발송',
})
const extraShipBusy = ref(false)

function loadLastCourier(): string {
  try {
    const raw = localStorage.getItem(LAST_COURIER_KEY)
    if (raw && isKnownMallCourier(raw)) return raw
  } catch {
    /* ignore */
  }
  return COURIERS[0]
}
function saveLastCourier(name: string) {
  try {
    localStorage.setItem(LAST_COURIER_KEY, name)
  } catch {
    /* ignore */
  }
}
function isDirectCourierName(courier: string) {
  return /직접/.test(courier || '')
}
function canExtraShip(item: MallOrderItemWithMargin) {
  return item.status === 'SHIPPING' || item.status === 'DELIVERED' || item.status === 'DONE'
}
function hasItemShipmentUi(item: MallOrderItemWithMargin) {
  return Boolean(item.invoice || item.shipments?.length || canExtraShip(item))
}
function hasItemPrimaryActions(item: MallOrderItemWithMargin) {
  return (
    isClaimPending(item) ||
    item.refundStatus === 'PENDING' ||
    (canAdminAdjust(item) && !hasItemShipmentUi(item))
  )
}
function openItemShipment(item: MallOrderItemWithMargin, row: ShipmentLedgerRow) {
  openTracking({
    idx: item.idx,
    courier: row.courier,
    invoice: row.invoice,
    name: item.name,
    orderCode: expandedDetail.value?.code,
  })
}
function openExtraShip(item: MallOrderItemWithMargin) {
  extraShipTarget.value = { ...item, orderCode: expandedDetail.value?.code }
  extraShipForm.courier = loadLastCourier()
  extraShipForm.invoice = ''
  extraShipForm.qty = 1
  extraShipForm.memo = '오발송·누락 재발송'
}
async function submitExtraShip() {
  if (!extraShipTarget.value || extraShipBusy.value) return
  const target = extraShipTarget.value
  const courier = extraShipForm.courier
  const invoice = extraShipForm.invoice.trim()
  const direct = isDirectCourierName(courier)
  const qty = Math.max(1, Math.trunc(Number(extraShipForm.qty) || 1))
  const memo = extraShipForm.memo.trim() || '오발송·누락 재발송'

  if (!direct && !invoice) {
    notify('추가 송장번호를 입력해 주세요. 직접배송은 생략할 수 있습니다.')
    return
  }
  if (!direct && isExistingWaybill(target, invoice)) {
    notify('이미 있는 송장입니다. 택배를 따로 보냈을 때만 다른 송장을 넣어 주세요.')
    return
  }
  if (qty > target.qty) {
    notify(`추가 수량은 주문 수량(${target.qty})을 넘을 수 없습니다.`)
    return
  }

  const ok = await confirmDialog({
    title: '재발송 등록',
    message: `${target.orderCode || expandedDetail.value?.code || ''} 주문에 재발송 송장을 추가할까요? 주문번호는 그대로입니다.`,
    detail: `${target.storeName}\n${target.name}\n${courier}${
      invoice ? `\n송장 ${invoice}` : '\n송장 없음(직접배송)'
    }\n수량 ${qty}\n사유 ${memo}`,
    confirmLabel: '등록',
    tone: 'primary',
  })
  if (!ok) return

  extraShipBusy.value = true
  try {
    await createAdminExtraShipment(target.idx, { courier, invoice, qty, memo })
    saveLastCourier(courier)
    notify('재발송 송장을 등록했습니다. 주문번호는 그대로입니다.')
    extraShipTarget.value = null
    await refreshExpanded()
    await reload()
  } catch (error) {
    notify(error instanceof Error ? error.message : '재발송 등록에 실패했습니다.')
  } finally {
    extraShipBusy.value = false
  }
}

/* ---- 환불 완료 처리(관리자) ---- */
const refundTarget = ref<MallOrderItemWithMargin | null>(null)
const refundForm = reactive({
  bank: '',
  account: '',
  holder: '',
  memo: '',
})
const refundBusy = ref(false)

function openRefundDone(item: MallOrderItemWithMargin) {
  refundTarget.value = item
  refundForm.bank = item.refundBank || ''
  refundForm.account = item.refundAccount || ''
  refundForm.holder = item.refundHolder || ''
  refundForm.memo = ''
}
function closeRefundDone() {
  refundTarget.value = null
}
function refundClaimLabelOf(item: MallOrderItemWithMargin) {
  if (item.claimType === 'RETURN') return '반품 환불'
  if (item.claimType === 'CANCEL') return '취소 환불'
  return '관리자 취소 환불'
}

const ADMIN_ADJUST_STATUSES: { value: MallOrderItemStatus; label: string }[] = [
  { value: 'PAID', label: '신규 주문' },
  { value: 'READY', label: '발송 준비' },
  { value: 'SHIPPING', label: '배송중' },
  { value: 'DELIVERED', label: '배송완료' },
  { value: 'DONE', label: '구매확정' },
]
const adjustTarget = ref<MallOrderItemWithMargin | null>(null)
const adjustBusy = ref(false)
const adjustForm = reactive({
  action: 'refund-done' as AdminAdjustAction,
  status: 'READY' as MallOrderItemStatus,
  reason: '',
  bank: '',
  account: '',
  holder: '',
})

function canAdminAdjust(item: MallOrderItemWithMargin) {
  return item.status !== 'WAITING' && item.refundStatus !== 'DONE'
}
function openAdjust(item: MallOrderItemWithMargin) {
  const flow = ADMIN_ADJUST_STATUSES.some((row) => row.value === item.status)
    ? item.status
    : item.claimPrevStatus && ADMIN_ADJUST_STATUSES.some((row) => row.value === item.claimPrevStatus)
      ? (item.claimPrevStatus as MallOrderItemStatus)
      : 'READY'
  adjustTarget.value = item
  adjustForm.action = 'refund-done'
  adjustForm.status = flow
  adjustForm.reason = ''
  adjustForm.bank = item.refundBank || ''
  adjustForm.account = item.refundAccount || ''
  adjustForm.holder =
    item.refundHolder || expandedDetail.value?.depositor || ''
}
function closeAdjust() {
  adjustTarget.value = null
}
const canSubmitAdjust = computed(() => {
  if (adjustBusy.value || !adjustForm.reason) return false
  if (adjustForm.action === 'refund-done') {
    if (expandedDetail.value?.payMethod === 'TOSS') return true
    return Boolean(adjustForm.bank && adjustForm.account && adjustForm.holder)
  }
  if (adjustForm.action === 'status') {
    return Boolean(adjustForm.status)
  }
  return true
})
const adjustSubmitLabel = computed(() => {
  if (adjustForm.action === 'refund-done') {
    return expandedDetail.value?.payMethod === 'TOSS' ? '카드 결제 취소' : '환불 완료로 표시'
  }
  if (adjustForm.action === 'refund-pending') return '환불 대기로 두기'
  return '상태 바꾸기'
})

async function submitAdjust() {
  if (!adjustTarget.value || !canSubmitAdjust.value) return
  const target = adjustTarget.value
  const action = adjustForm.action
  const statusLabel =
    ADMIN_ADJUST_STATUSES.find((row) => row.value === adjustForm.status)?.label || adjustForm.status
  const ok = await confirmDialog({
    title: action === 'status' ? '배송 상태 변경' : '환불 처리',
    message:
      action === 'refund-done'
        ? expandedDetail.value?.payMethod === 'TOSS'
          ? '이 상품을 취소하고 결제했던 카드·간편결제로 환불할까요? 되돌릴 수 없습니다.'
          : '이 상품을 취소하고 환불 완료로 표시할까요? 되돌릴 수 없습니다.'
        : action === 'refund-pending'
          ? '이 상품을 취소하고 환불 대기로 둘까요?'
          : `배송 상태를 「${statusLabel}」로 바꿀까요?`,
    detail: [
      expandedDetail.value?.code,
      target.name,
      `${formatWon(target.refundAmount || target.amount || 0)}원`,
      adjustForm.reason,
    ]
      .filter(Boolean)
      .join('\n'),
    confirmLabel: adjustSubmitLabel.value,
    tone: action === 'status' ? 'primary' : 'danger',
  })
  if (!ok) return

  adjustBusy.value = true
  try {
    const message = await adjustAdminOrderItem(target.idx, {
      action,
      status: action === 'status' ? adjustForm.status : undefined,
      reason: adjustForm.reason,
      refundBank: adjustForm.bank,
      refundAccount: adjustForm.account,
      refundHolder: adjustForm.holder,
      memo: adjustForm.reason,
    })
    notify(message)
    adjustTarget.value = null
    await refreshExpanded()
    await reload()
  } catch (error) {
    notify(error instanceof Error ? error.message : '주문 조정에 실패했습니다.')
  } finally {
    adjustBusy.value = false
  }
}

/* ---- 취소·반품 승인/반려(클레임 처리) ---- */
const claimRejectTarget = ref<MallOrderItemWithMargin | null>(null)
const claimRejectReason = ref('')
const claimBusy = ref(0)

function isClaimPending(item: MallOrderItemWithMargin) {
  return item.status === 'CANCEL_REQUEST' || item.status === 'RETURN_REQUEST'
}
function isItemActionBusy(item: MallOrderItemWithMargin) {
  return isClaimPending(item) || item.refundStatus === 'PENDING'
}
function isClaimRejected(item: MallOrderItemWithMargin) {
  return Boolean(item.claimRejectReason) && !isClaimPending(item) && item.status !== 'CANCELED'
}
function claimRequestLabel(item: MallOrderItemWithMargin) {
  if (item.status === 'CANCEL_REQUEST') return '취소 요청'
  if (item.status === 'RETURN_REQUEST') return '반품 요청'
  if (item.claimType === 'CANCEL') return '취소 처리'
  if (item.claimType === 'RETURN') return '반품 처리'
  return '클레임'
}
function claimAgeHoursOf(item: MallOrderItemWithMargin): number {
  if (!item.claimAt) return 0
  const ms = Date.now() - new Date(item.claimAt).getTime()
  return Math.max(0, Math.floor(ms / 3600000))
}
function claimSlaLabel(item: MallOrderItemWithMargin) {
  const hours = claimAgeHoursOf(item)
  if (!hours) return '방금'
  if (hours < 24) return `${hours}시간 경과`
  const days = Math.floor(hours / 24)
  return `${days}일 경과`
}
function claimSlaTone(item: MallOrderItemWithMargin): '' | 'warn' | 'alert' {
  const hours = claimAgeHoursOf(item)
  if (hours >= 48) return 'alert'
  if (hours >= 24) return 'warn'
  return ''
}

async function approveClaim(item: MallOrderItemWithMargin) {
  if (claimBusy.value) return
  const isCancel = item.status === 'CANCEL_REQUEST'
  const label = isCancel ? '취소 요청 승인' : '반품 요청 승인'
  const detail = [
    expandedDetail.value?.code,
    item.storeName,
    `${item.name} · ${item.qty}개`,
    `${formatWon(item.refundAmount || item.amount)}원`,
    item.claimType === 'RETURN'
      ? item.returnCause === 'CHANGE'
        ? `단순 변심 · 판매자 반품비 ${formatWon(item.appliedReturnFee || 0)}원`
        : '하자·오배송 · 판매자 상품 정산 없음'
      : '',
    item.claimReason ? `사유: ${item.claimReason}` : '',
    isCancel
      ? expandedDetail.value?.payMethod === 'TOSS'
        ? '재고가 자동 복구되고, 결제했던 카드·간편결제로 환불됩니다.'
        : '재고가 자동 복구되고, 환불 대기로 잡힙니다.'
      : expandedDetail.value?.payMethod === 'TOSS'
        ? '반품은 재고가 자동 복구되지 않습니다. 결제했던 카드·간편결제로 환불됩니다.'
        : '반품은 재고가 자동 복구되지 않습니다. 환불 대기로 잡힙니다.',
  ]
    .filter(Boolean)
    .join('\n')

  const ok = await confirmDialog({
    title: label,
    message: `${label} 처리할까요?`,
    detail,
    confirmLabel: '승인',
    tone: 'primary',
  })
  if (!ok) return

  claimBusy.value = item.idx
  try {
    const message = await approveAdminClaim(item.idx)
    notify(message || '승인했습니다.')
    await refreshExpanded()
    await reload()
  } catch (error) {
    notify(error instanceof Error ? error.message : '승인에 실패했습니다.')
  } finally {
    claimBusy.value = 0
  }
}

function openClaimReject(item: MallOrderItemWithMargin) {
  claimRejectTarget.value = item
  claimRejectReason.value = ''
}
function closeClaimReject() {
  claimRejectTarget.value = null
  claimRejectReason.value = ''
}
async function submitClaimReject() {
  if (!claimRejectTarget.value || claimBusy.value) return
  const target = claimRejectTarget.value
  const reason = claimRejectReason.value.trim()
  if (!reason) {
    notify('반려 사유를 입력해 주세요.')
    return
  }

  const ok = await confirmDialog({
    title: '클레임 반려',
    message: `${claimRequestLabel(target)}을(를) 반려할까요?`,
    detail: [
      expandedDetail.value?.code,
      target.storeName,
      `${target.name} · ${target.qty}개`,
      `반려 사유: ${reason}`,
      '이전 배송 상태로 되돌립니다.',
    ]
      .filter(Boolean)
      .join('\n'),
    confirmLabel: '반려',
    tone: 'danger',
  })
  if (!ok) return

  claimBusy.value = target.idx
  try {
    const message = await rejectAdminClaim(target.idx, reason)
    notify(message || '반려했습니다.')
    claimRejectTarget.value = null
    claimRejectReason.value = ''
    await refreshExpanded()
    await reload()
  } catch (error) {
    notify(error instanceof Error ? error.message : '반려에 실패했습니다.')
  } finally {
    claimBusy.value = 0
  }
}

async function submitRefundDone() {
  if (!refundTarget.value || refundBusy.value) return
  if (!refundForm.bank || !refundForm.account || !refundForm.holder) return
  const target = refundTarget.value

  const ok = await confirmDialog({
    title: '환불 송금 완료',
    message: '이 건을 송금 완료로 표시할까요?',
    detail: [
      expandedDetail.value?.code,
      target.name,
      `${formatWon(target.refundAmount || target.amount || 0)}원`,
      `${refundForm.bank} ${refundForm.account} (${refundForm.holder})`,
    ]
      .filter(Boolean)
      .join('\n'),
    confirmLabel: '송금 완료',
    tone: 'primary',
  })
  if (!ok) return

  refundBusy.value = true
  try {
    await markAdminRefundDone(target.idx, {
      memo: refundForm.memo,
      refundBank: refundForm.bank,
      refundAccount: refundForm.account,
      refundHolder: refundForm.holder,
    })
    notify('환불 완료로 표시했습니다.')
    refundTarget.value = null
    await refreshExpanded()
    await reload()
  } catch (error) {
    notify(error instanceof Error ? error.message : '환불 완료 처리에 실패했습니다.')
  } finally {
    refundBusy.value = false
  }
}

function trackItemsOf(order: AdminMallOrder) {
  return order.trackItems || []
}

async function onCloseTracking() {
  closeTracking()
  await reload()
  await refreshExpanded()
}

function openTrackingFromOrder(order: AdminMallOrder) {
  const first = trackItemsOf(order)[0]
  if (!first) return
  openTracking({
    idx: first.idx,
    courier: first.courier,
    invoice: first.invoice,
    name: first.name,
    orderCode: order.code,
  })
}
const cancelReason = ref('')
/*
 * 초기값을 true 로 둔다. onMounted 의 reload() 가 시작되기 전 짧은 순간에
 * displayOrders.length === 0 & loading=false 조합이 렌더링되면
 * 「조건에 맞는 주문이 없습니다.」 가 잠깐 번쩍이며 사용자가 주문번호 딥링크로
 * 넘어왔는데도 「없음」 상태로 오해할 수 있다. reload가 실패하면 즉시 false 로 내려간다.
 */
const loading = ref(true)
const errorMessage = ref('')
const working = ref('')
const adminMemoDraft = ref('')
const memoBusy = ref(false)

type OrderSectionKey = 'notified' | 'unnotified' | 'shipping' | 'done' | 'canceled'
type WorkFilterKey = 'refund' | 'claim'

/**
 * 기존 URL을 생명주기 키로 변환한다.
 * section=unpaid&notified=1 → 입금 알림, section=unpaid → 입금 미진행.
 * section=refund 는 처리할 일(work=refund)로 옮겼다.
 */
function normalizeSectionParam(
  raw: string,
  notifiedRaw = String(route.query.notified ?? ''),
): OrderSectionKey | '' {
  const notified = notifiedRaw === '1'
  const upper = raw.toUpperCase()
  if (upper === 'PENDING') return notified ? 'notified' : 'unnotified'
  if (upper === 'PAID') return 'shipping'
  if (upper === 'CANCELED') return 'canceled'
  const lower = raw.toLowerCase()
  if (lower === 'new') return 'shipping'
  if (lower === 'refund') return ''
  if (lower === 'unpaid') return notified ? 'notified' : 'unnotified'
  if (['notified', 'unnotified', 'shipping', 'done', 'canceled'].includes(lower)) {
    return lower as OrderSectionKey
  }
  return ''
}

function readWorkFromQuery(
  workRaw = String(route.query.work ?? ''),
  sectionRaw = String(route.query.section ?? ''),
): WorkFilterKey | '' {
  const fromWork = workRaw.toLowerCase()
  if (fromWork === 'refund' || fromWork === 'claim') return fromWork
  if (sectionRaw.toLowerCase() === 'refund') return 'refund'
  return ''
}

const taxOnly = ref(String(route.query.tax ?? '') === '1')
const work = ref<WorkFilterKey | ''>(taxOnly.value ? '' : readWorkFromQuery())
const status = ref<OrderSectionKey | ''>(
  taxOnly.value || work.value
    ? ''
    : normalizeSectionParam(
        String(route.query.section ?? route.query.status ?? ''),
        String(route.query.notified ?? ''),
      ),
)
const keyword = ref(String(route.query.keyword ?? ''))

/*
 * keywordFocusCode 는 displayOrders 를 참조하므로, 그 정의가 뒤에 있는
 * 지금 이 자리에서 computed 를 만들면 `watch(keywordFocusCode, …)` 등록 시점에
 * baseline 을 잡느라 getter 가 즉시 한 번 호출되면서 TDZ(ReferenceError:
 * "Cannot access 'displayOrders' before initialization") 로 setup 이 통째로
 * 중단된다 (컴포넌트가 「<!---->」 로만 렌더되어 화면이 텅 빈다).
 * 그래서 keywordFocusCode 와 그 감시자는 displayOrders 정의 이후로 옮겨 둔다.
 */

const taxPendingCount = computed(() => counts.value.taxInvoicePending ?? 0)

function taxLabelOf(order: AdminMallOrder): string {
  return MALL_TAX_STATUS_LABEL[effectiveTaxStatus(order)] || '미요청'
}

function effectiveTaxStatus(order: AdminMallOrder): string {
  return order.taxStatus || (order.taxInvoice ? 'REQUESTED' : 'NONE')
}

function taxDisplayEmail(order: AdminMallOrder): string {
  return String(order.taxEmail || order.memberEmail || '').trim()
}

function taxDisplayBizCert(order: AdminMallOrder): string {
  return String(order.bizCert || order.memberBizCert || '').trim()
}

function taxIssuesOf(order: AdminMallOrder): string[] {
  return listTaxInvoiceIssues({
    bizName: order.bizName,
    bizNum: order.bizNum,
    taxEmail: order.taxEmail,
    bizCert: order.bizCert,
  })
}

function canCancelOrder(order: AdminMallOrder): boolean {
  return !orderHasDelivered(order)
}

const taxRejectIssues = computed(() => {
  if (!taxRejectTarget.value) return [] as string[]
  const issues = [...taxIssuesOf(taxRejectTarget.value)]
  const extra = taxRejectExtra.value.trim()
  if (extra) issues.push(extra)
  return issues
})

watch(taxRejectIssues, (issues) => {
  if (!taxRejectTarget.value) return
  taxRejectSms.value = buildTaxRejectSms(taxRejectTarget.value.code, issues)
})

function taxToneOf(order: AdminMallOrder): string {
  const status = effectiveTaxStatus(order)
  if (status === 'DONE') return 'sui-badge--green'
  if (status === 'REQUESTED') return 'sui-badge--violet'
  return 'sui-badge--gray'
}

function patchOrderInList(order: AdminMallOrder) {
  const index = orders.value.findIndex((row) => row.code === order.code)
  if (index >= 0) {
    orders.value[index] = { ...orders.value[index], ...order }
  }
}

async function onTaxComplete(order: AdminMallOrder) {
  const ok = await confirmDialog({
    title: '세금계산서 발행 완료',
    message: `${order.code} 주문의 세금계산서 발행을 완료 처리할까요?`,
    confirmLabel: '발행 완료',
  })
  if (!ok) return

  working.value = order.code
  try {
    const result = await saveAdminTaxStatus(order.code, 'DONE', {
      bizNum: order.bizNum,
      bizName: order.bizName,
    })
    notify('세금계산서 발행을 완료 처리했습니다.')
    patchOrderInList(result.order)
    if (taxTarget.value?.code === order.code) {
      if (taxOnly.value) {
        closeTaxModal()
      } else {
        taxTarget.value = result.order
      }
    }
    await reload()
  } catch (error) {
    notify(error instanceof Error ? error.message : '세금계산서 상태를 저장하지 못했습니다.')
  } finally {
    working.value = ''
  }
}

async function onTaxUndo(order: AdminMallOrder) {
  const ok = await confirmDialog({
    title: '발행 완료 취소',
    message: `${order.code} 주문의 세금계산서 발행 완료를 취소하고 발행 요청 상태로 되돌릴까요?`,
    confirmLabel: '완료 취소',
    tone: 'danger',
  })
  if (!ok) return

  working.value = order.code
  try {
    const result = await saveAdminTaxStatus(order.code, 'REQUESTED', {
      bizNum: order.bizNum,
      bizName: order.bizName,
    })
    notify('발행 완료를 취소했습니다.')
    patchOrderInList(result.order)
    if (taxTarget.value?.code === order.code) {
      taxTarget.value = result.order
    }
    await reload()
  } catch (error) {
    notify(error instanceof Error ? error.message : '세금계산서 상태를 저장하지 못했습니다.')
  } finally {
    working.value = ''
  }
}

async function openTaxModal(order: AdminMallOrder) {
  taxTarget.value = order
  taxLoading.value = true
  try {
    taxTarget.value = await fetchAdminOrder(order.code)
  } catch (error) {
    notify(error instanceof Error ? error.message : '세금계산서 정보를 불러오지 못했습니다.')
  } finally {
    taxLoading.value = false
  }
}

function closeTaxModal() {
  taxTarget.value = null
  taxLoading.value = false
}

function openDetailFromTax() {
  const code = taxTarget.value?.code
  closeTaxModal()
  if (!code) return
  const order =
    displayOrders.value.find((row) => row.code === code)
    || orders.value.find((row) => row.code === code)
  if (order) void expandOrder(order, { scroll: true })
}

function openTaxReject(order: AdminMallOrder) {
  taxRejectTarget.value = order
  taxRejectExtra.value = ''
  taxRejectSendSms.value = Boolean(order.phone)
  const issues = taxIssuesOf(order)
  taxRejectSms.value = buildTaxRejectSms(order.code, issues)
}

function closeTaxReject() {
  taxRejectTarget.value = null
  taxRejectExtra.value = ''
  taxRejectSms.value = ''
}

async function submitTaxReject() {
  if (!taxRejectTarget.value || working.value) return
  const issues = taxRejectIssues.value
  if (!issues.length) {
    notify('반려 사유를 입력해 주세요.')
    return
  }

  const ok = await confirmDialog({
    title: '발행 요청 반려',
    message: `${taxRejectTarget.value.code} 세금계산서 발행 요청을 반려할까요?`,
    detail: taxRejectSendSms.value
      ? `아래 내용으로 문자를 보냅니다.\n\n${taxRejectSms.value}`
      : '문자는 보내지 않습니다.',
    confirmLabel: '반려',
    tone: 'danger',
  })
  if (!ok) return

  working.value = taxRejectTarget.value.code
  try {
    const result = await rejectAdminTaxInvoice(taxRejectTarget.value.code, {
      issues,
      smsMessage: taxRejectSms.value,
      sendSms: taxRejectSendSms.value,
    })
    notify(result.message || '세금계산서 발행 요청을 반려했습니다.')
    patchOrderInList(result.order)
    closeTaxReject()
    closeTaxModal()
    await reload()
  } catch (error) {
    notify(error instanceof Error ? error.message : '세금계산서 발행 요청을 반려하지 못했습니다.')
  } finally {
    working.value = ''
  }
}

const ITEM_TONE: Record<MallOrderItemStatus, string> = {
  WAITING: 'sui-badge--gray',
  PAID: 'sui-badge--blue',
  READY: 'sui-badge--violet',
  SHIPPING: 'sui-badge--amber',
  DELIVERED: 'sui-badge--green',
  DONE: 'sui-badge--green',
  CANCEL_REQUEST: 'sui-badge--red',
  RETURN_REQUEST: 'sui-badge--red',
  CANCELED: 'sui-badge--gray',
}

const ITEM_FLOW: MallOrderItemStatus[] = ['PAID', 'READY', 'SHIPPING', 'DELIVERED', 'DONE']

const fulfillSteps = [
  { key: 'confirm' as const, label: '결제 확인' },
  { key: 'ready' as const, label: '업체 확인' },
  { key: 'ship' as const, label: '발송' },
  { key: 'deliver' as const, label: '배송완료' },
]

function itemTone(value: MallOrderItemStatus): string {
  return ITEM_TONE[value] ?? 'sui-badge--gray'
}

/** 이 화면은 배송 여부만 본다. 구매확정은 배송완료와 같이 표시한다. */
function itemStatusLabel(value: MallOrderItemStatus): string {
  if (value === 'DONE') return MALL_ORDER_ITEM_STATUS_LABEL.DELIVERED
  return MALL_ORDER_ITEM_STATUS_LABEL[value]
}

function orderItemStatuses(order: AdminMallOrder): MallOrderItemStatus[] {
  return (order.items?.map((item) => item.status) ?? order.itemStatuses ?? []) as MallOrderItemStatus[]
}

function refundCountsOf(order: AdminMallOrder) {
  const fromItems = order.items || []
  const pendingFromItems = fromItems.filter((item) => item.refundStatus === 'PENDING').length
  const doneFromItems = fromItems.filter((item) => item.refundStatus === 'DONE').length
  return {
    pending: Math.max(Number(order.refundPendingCount) || 0, pendingFromItems),
    done: Math.max(Number(order.refundDoneCount) || 0, doneFromItems),
  }
}

function refundSummaryOf(order: AdminMallOrder | null | undefined) {
  if (!order) return null
  const { pending, done } = refundCountsOf(order)
  if (pending > 0) {
    // 환불 대기는 「승인된 취소·반품 → 송금 필요」 상태로 가장 긴급하다.
    // 총 환불 금액을 함께 표시해서 담당자가 규모를 즉시 파악할 수 있게 한다.
    const pendingAmount = (order.items || [])
      .filter((item) => item.refundStatus === 'PENDING')
      .reduce((sum, item) => sum + (Number(item.refundAmount) || Number(item.amount) || 0), 0)
    const countLabel = pending > 1 ? ` ${pending}건` : ''
    const amountLabel = pendingAmount > 0 ? ` · ${formatWon(pendingAmount)}원` : ''
    return {
      label: `환불 대기${countLabel}${amountLabel}`,
      tone: 'aord__refund-chip--strong',
      status: 'PENDING' as const,
    }
  }
  if (done > 0) {
    return {
      label: done > 1 ? `환불 완료 ${done}` : '환불 완료',
      tone: 'aord__refund-chip--done',
      status: 'DONE' as const,
    }
  }
  return null
}

/** 입금 전에 취소되어 돌려줄 돈이 없는 주문 */
function isUnpaidCancel(order: AdminMallOrder) {
  if (order.status !== 'CANCELED' || order.paidAt) return false
  const { pending, done } = refundCountsOf(order)
  return pending === 0 && done === 0
}

function activeClaimLabel(order: AdminMallOrder): string {
  const statuses = orderItemStatuses(order)
  const hasCancel = statuses.includes('CANCEL_REQUEST')
  const hasReturn = statuses.includes('RETURN_REQUEST')
  if (hasCancel && hasReturn) return '취소·반품 대기'
  if (hasCancel) return '취소 대기'
  if (hasReturn) return '반품 대기'
  return '처리 대기'
}

function progressOf(order: AdminMallOrder) {
  const statuses = orderItemStatuses(order)
  const hasClaim = statuses.some(
    (itemStatus) => itemStatus === 'CANCEL_REQUEST' || itemStatus === 'RETURN_REQUEST',
  )
  const canceledItems = statuses.filter((itemStatus) => itemStatus === 'CANCELED').length
  const activeItems = statuses.filter((itemStatus) => itemStatus !== 'CANCELED').length

  if (order.status === 'CANCELED' || (statuses.length > 0 && activeItems === 0)) {
    return {
      title: '주문 취소',
      tone: 'sui-badge--gray',
      hint: isUnpaidCancel(order)
        ? order.payMethod === 'TOSS'
          ? '결제 전에 취소되어 환불이 없습니다.'
          : '입금 전에 취소되어 환불이 없습니다.'
        : '이 주문은 취소되었습니다.',
    }
  }
  if (canceledItems > 0 && activeItems > 0) {
    return {
      title: '부분 취소',
      tone: 'sui-badge--red',
      hint: `상품 ${canceledItems}건이 취소되었습니다.`,
    }
  }
  if (order.status === 'PENDING' && order.payMethod === 'TOSS') {
    return { title: '결제 대기', tone: 'sui-badge--amber', hint: '구매자가 카드·간편결제를 아직 마치지 않았습니다.' }
  }
  if (order.status === 'PENDING' && order.payNotifiedAt) {
    return { title: '입금 알림', tone: 'sui-badge--violet', hint: '구매 기관이 입금했다고 알렸습니다. 통장을 확인해 주세요.' }
  }
  if (order.status === 'PENDING') {
    return { title: '입금 미진행', tone: 'sui-badge--amber', hint: '아직 「입금했어요」를 누르지 않았습니다.' }
  }
  if (hasClaim) {
    const claim = statuses.find(
      (itemStatus) => itemStatus === 'CANCEL_REQUEST' || itemStatus === 'RETURN_REQUEST',
    )
    return {
      title: claim === 'RETURN_REQUEST' ? '반품 요청' : '취소 요청',
      tone: 'sui-badge--red',
      hint: '클레임이 진행 중입니다.',
    }
  }

  const furthest = furthestItemStatus(order)
  if (furthest === 'READY') {
    return { title: '발송 준비', tone: itemTone('READY'), hint: '업체가 주문을 확인했습니다. 발송 전입니다.' }
  }
  if (furthest === 'SHIPPING') {
    return { title: '배송중', tone: itemTone('SHIPPING'), hint: '업체가 발송했습니다. 도착하면 자동으로 배송완료됩니다.' }
  }
  if (furthest === 'DELIVERED' || furthest === 'DONE') {
    return { title: '배송완료', tone: itemTone('DELIVERED'), hint: '배송이 완료되었습니다.' }
  }
  return { title: '업체 미확인', tone: itemTone('PAID'), hint: '결제확인됨 · 판매업체가 아직 주문 확인 전입니다.' }
}

function furthestItemStatus(order: AdminMallOrder): MallOrderItemStatus | '' {
  let best = -1
  let value: MallOrderItemStatus | '' = ''
  orderItemStatuses(order).forEach((itemStatus) => {
    const index = ITEM_FLOW.indexOf(itemStatus)
    if (index > best) {
      best = index
      value = itemStatus
    }
  })
  return value
}

function stepState(
  order: AdminMallOrder,
  step: 'confirm' | 'ready' | 'ship' | 'deliver',
) {
  if (order.status === 'CANCELED') {
    return ''
  }
  if (order.status === 'PENDING') {
    return step === 'confirm' ? 'current' : ''
  }
  const furthest = furthestItemStatus(order)
  const index = ITEM_FLOW.indexOf(furthest)
  if (step === 'confirm') return 'done'
  if (step === 'ready') {
    if (index >= 1) return 'done'
    if (index === 0) return 'current'
    return ''
  }
  if (step === 'ship') {
    if (index >= 2) return 'done'
    if (index === 1) return 'current'
    return ''
  }
  if (index >= 3) return 'done'
  if (index === 2) return 'current'
  return ''
}

function canUnconfirm(order: AdminMallOrder): boolean {
  if (order.status !== 'PAID' || order.payMethod === 'TOSS') return false
  const statuses = order.items?.map((item) => item.status) ?? order.itemStatuses ?? []
  if (!statuses.length) return true
  return statuses.every((status) => status === 'PAID' || status === 'WAITING')
}

function hasActiveClaim(order: AdminMallOrder) {
  return orderItemStatuses(order).some(
    (itemStatus) => itemStatus === 'CANCEL_REQUEST' || itemStatus === 'RETURN_REQUEST',
  )
}
function qnaOpenCountOf(order: AdminMallOrder) {
  return Math.max(Number(order.qnaOpenCount) || 0, order.openQnas?.length || 0)
}
function openQnasOf(order: AdminMallOrder) {
  return order.openQnas || []
}
/** 취소·반품 대기이거나 미답변 상품문의가 있으면 클레임·문의 조건에 넣는다. */
function hasClaimWork(order: AdminMallOrder) {
  return hasActiveClaim(order) || qnaOpenCountOf(order) > 0
}

/**
 * 상단 주문 상태 pill과 아래 섹션을 일대일 매핑한다.
 * counts는 서버 status 기준이 아니라, 클라이언트에서 sectionKeyOf로 실제 분류한 결과다.
 */
const sectionCounts = computed<Record<OrderSectionKey, number>>(() => {
  const buckets: Record<OrderSectionKey, number> = {
    notified: 0,
    unnotified: 0,
    shipping: 0,
    done: 0,
    canceled: 0,
  }
  orders.value.forEach((order) => {
    buckets[sectionKeyOf(order)] += 1
  })
  return buckets
})

const workCounts = computed(() => {
  let refund = 0
  let claim = 0
  orders.value.forEach((order) => {
    if (refundCountsOf(order).pending > 0) refund += 1
    if (hasClaimWork(order)) claim += 1
  })
  return { refund, claim }
})

const statusTabs = computed(() => {
  const total = orders.value.length
  return [
    { key: '' as OrderSectionKey | '', label: '전체', count: total, tone: '' as const },
    ...ORDER_SECTION_META.map((meta) => ({
      key: meta.key as OrderSectionKey | '',
      label: meta.title,
      count: sectionCounts.value[meta.key],
      tone: meta.tone,
    })),
  ]
})

const workTabs = computed(() => [
  {
    key: 'refund' as const,
    label: '환불 대기',
    count: workCounts.value.refund,
    tone: 'red' as const,
  },
  {
    key: 'claim' as const,
    label: '클레임 · 문의',
    count: workCounts.value.claim,
    tone: 'amber' as const,
  },
])

const displayOrders = computed(() => {
  let rows = orders.value
  if (work.value === 'refund') {
    rows = rows.filter((order) => refundCountsOf(order).pending > 0)
  } else if (work.value === 'claim') {
    rows = rows.filter((order) => hasClaimWork(order))
  } else if (status.value) {
    rows = rows.filter((order) => sectionKeyOf(order) === status.value)
  }
  return rows
})

/*
 * keyword 로 정확히 한 건이 걸리면 그 코드를 반환해, 「검색된 주문」 뱃지·자동 확장의 힌트로 쓴다.
 * 이 computed 와 아래 watch 는 displayOrders 뒤에 정의해야 한다.
 * (앞에 두면 watch 등록 시 baseline 을 위해 getter 를 한 번 호출하는데
 *  그때 displayOrders 가 아직 TDZ 라 setup 전체가 죽는다.)
 */
const keywordFocusCode = computed(() => {
  const code = keyword.value.trim()
  if (!code) return ''
  const matched = displayOrders.value.filter((order) => order.code === code)
  return matched.length === 1 ? code : ''
})

// 주문번호로 정확히 1건 검색된 경우 자동으로 상세 확장 + 카드 스크롤
// (다른 페이지에서 keyword 파라미터로 넘어왔을 때 즉시 상세가 보이도록 통일)
watch(keywordFocusCode, (code) => {
  if (!code) return
  const order = displayOrders.value.find((o) => o.code === code)
  if (!order) return
  if (expandedCode.value === code) {
    // 이미 열려 있어도 스크롤은 다시 맞춰 준다 (긴 목록에서 놓칠 수 있으므로)
    void nextTick().then(() => {
      document.getElementById(`aord-detail-${code}`)?.scrollIntoView({
        block: 'center',
        behavior: 'smooth',
      })
    })
    return
  }
  void expandOrder(order, { scroll: true })
})

/**
 * 주문을 생명주기 중 하나로만 분류한다.
 * 입금 전은 「입금했어요」 여부로 입금 알림 / 입금 미진행을 나눈다.
 */
function sectionKeyOf(order: AdminMallOrder): OrderSectionKey {
  if (order.status === 'CANCELED') return 'canceled'
  if (order.status === 'PENDING') return order.payNotifiedAt ? 'notified' : 'unnotified'
  const furthest = furthestItemStatus(order)
  if (furthest === 'DELIVERED' || furthest === 'DONE') return 'done'
  return 'shipping'
}

interface OrderSectionMeta {
  key: OrderSectionKey
  title: string
  desc: string
  icon: string
  tone: 'red' | 'amber' | 'violet' | 'blue' | 'gray' | 'slate'
}

const ORDER_SECTION_META: OrderSectionMeta[] = [
  {
    key: 'notified',
    title: '입금 알림',
    desc: '구매 기관이 「입금했어요」를 눌렀습니다. 통장을 확인해 주세요',
    icon: 'mdi-bell-ring-outline',
    tone: 'violet',
  },
  {
    key: 'unnotified',
    title: '결제 전',
    desc: '토스페이먼츠 결제 전입니다. 이전 계좌이체 주문은 「입금했어요」 전입니다',
    icon: 'mdi-cash-clock',
    tone: 'amber',
  },
  {
    key: 'shipping',
    title: '배송 진행',
    desc: '결제확인 완료 · 업체 미확인 · 발송 준비 · 배송중',
    icon: 'mdi-truck-delivery-outline',
    tone: 'blue',
  },
  {
    key: 'done',
    title: '완료',
    desc: '배송완료 · 참조용',
    icon: 'mdi-check-all',
    tone: 'gray',
  },
  {
    key: 'canceled',
    title: '취소',
    desc: '주문 취소 · 결제확인 이전 자동 취소 · 참조용',
    icon: 'mdi-close-circle-outline',
    tone: 'slate',
  },
]

const orderSections = computed(() => {
  const buckets: Record<OrderSectionKey, AdminMallOrder[]> = {
    notified: [],
    unnotified: [],
    shipping: [],
    done: [],
    canceled: [],
  }
  displayOrders.value.forEach((order) => {
    buckets[sectionKeyOf(order)].push(order)
  })
  return ORDER_SECTION_META.map((meta) => ({
    ...meta,
    orders: buckets[meta.key],
  })).filter((section) => section.orders.length > 0)
})

function syncQuery() {
  const query: Record<string, string> = {}
  if (taxOnly.value) {
    query.tax = '1'
  } else if (work.value) {
    query.work = work.value
  } else if (status.value) {
    query.section = status.value
  }
  if (keyword.value.trim()) query.keyword = keyword.value.trim()
  router.replace({ path: '/admin/orders', query })
}

function clearKeyword() {
  keyword.value = ''
  syncQuery()
  void reload()
}

async function reload() {
  loading.value = true
  errorMessage.value = ''

  try {
    // 서버 status 필터는 사용하지 않는다. 상단 pill은 아래 섹션과 매칭되는
    // 클라이언트 분류(sectionKeyOf) 기준이므로, 서버는 항상 전체를 내려주고
    // pill 클릭은 displayOrders에서 필터한다. counts도 클라이언트에서 재집계한다.
    const result = await fetchAdminOrders({
      keyword: keyword.value,
      from: fromDate.value,
      to: toDate.value,
      tax: taxOnly.value,
    })
    orders.value = result.orders
    counts.value = result.counts
    await refreshExpanded()
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '주문을 불러오지 못했습니다.'
    orders.value = []
  } finally {
    loading.value = false
  }
}

function setStatus(next: OrderSectionKey | '') {
  taxOnly.value = false
  work.value = ''
  status.value = next
  syncQuery()
  void reload()
}

function setWork(next: WorkFilterKey) {
  taxOnly.value = false
  work.value = next
  status.value = ''
  syncQuery()
  void reload()
}

function setTaxOnly(active: boolean) {
  taxOnly.value = active
  if (active) {
    status.value = ''
    work.value = ''
  }
  syncQuery()
  void reload()
}

function onPreset(key: DatePresetKey) {
  applyPreset(key)
  void reload()
}

function onDateChange() {
  onManualDateChange()
  void reload()
}

function downloadCsv() {
  const rows = displayOrders.value
  if (!rows.length) return
  const header = taxOnly.value
    ? [
        '주문번호',
        '주문일시',
        '회원명',
        '입금자',
        '상호',
        '사업자번호',
        '수신이메일',
        '사업자등록증',
        '입금액',
      ]
    : [
        '주문번호',
        '결제수단',
        '주문일시',
        '처리현황',
        '회원명',
        '수령인',
        '연락처',
        '입금자',
        '상품요약',
        '상품수',
        '상품금액',
        '배송비',
        '결제금액',
        '세금계산서',
        '발행완료일시',
        '입금알림',
        '결제확인일시',
      ]
  const body = rows.map((order) => {
    const cells = taxOnly.value
      ? [
          order.code,
          order.orderedAt || '',
          order.orgName || '',
          order.depositor || '',
          order.bizName || '',
          order.bizNum || '',
          order.taxEmail || '',
          order.bizCert ? bizCertHref(order.bizCert) : '',
          order.totalAmount,
        ]
      : [
          order.code,
          mallPayMethodLabel(order),
          order.orderedAt || '',
          progressOf(order).title,
          order.orgName || '',
          order.receiver || '',
          order.phone || '',
          order.depositor || '',
          order.itemSummary || '',
          order.itemCount,
          order.itemAmount,
          order.shippingFee,
          order.totalAmount,
          taxLabelOf(order),
          effectiveTaxStatus(order) === 'DONE' && order.taxAt ? order.taxAt : '',
          order.payNotifiedAt || '',
          order.paidAt || '',
        ]
    return cells.map((cell) => csvEscape(cell)).join(',')
  })
  const csv = `\uFEFF${[header.join(','), ...body].join('\n')}`
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `관리자주문_${new Date().toISOString().slice(0, 10)}.csv`
  anchor.click()
  URL.revokeObjectURL(url)
}

onMounted(async () => {
  await reload()
  try {
    payment.value = await fetchPaymentInfo()
  } catch {
    payment.value = null
  }
})

watch(
  () => [
    route.query.section,
    route.query.status,
    route.query.work,
    route.query.notified,
    route.query.tax,
    route.query.keyword,
  ],
  () => {
    const nextTax = String(route.query.tax ?? '') === '1'
    const nextWork = nextTax
      ? ''
      : readWorkFromQuery(String(route.query.work ?? ''), String(route.query.section ?? ''))
    const nextStatus =
      nextTax || nextWork
        ? ''
        : normalizeSectionParam(
            String(route.query.section ?? route.query.status ?? ''),
            String(route.query.notified ?? ''),
          )
    const nextKeyword = String(route.query.keyword ?? '')
    let changed = false
    if (nextTax !== taxOnly.value) {
      taxOnly.value = nextTax
      changed = true
    }
    if (nextWork !== work.value) {
      work.value = nextWork
      changed = true
    }
    if (nextStatus !== status.value) {
      status.value = nextStatus
      changed = true
    }
    if (nextKeyword !== keyword.value) {
      keyword.value = nextKeyword
      changed = true
      /*
       * CS·정산 등에서 「관련 주문」 링크로 다시 넘어올 때는 이미 열려 있던
       * 주문 페이지 인스턴스를 재사용하므로 setup은 재실행되지 않는다.
       * 그동안 사용자가 좁혀 둔 기간 필터에 가려 검색 결과가 비지 않도록,
       * keyword 만 있고 preset이 명시되지 않았으면 「전체」로 다시 넓혀 준다.
       */
      expandRangeForDeepLink()
    }
    if (changed) void reload()
  },
)

async function expandOrder(order: AdminMallOrder, opts?: { scroll?: boolean }) {
  const alreadyOpen =
    expandedCode.value === order.code && (expandedDetail.value || expandedLoading.value)
  if (!alreadyOpen) {
    expandedCode.value = order.code
    expandedDetail.value = null
    expandedError.value = ''
    expandedLoading.value = true
    try {
      const loaded = await fetchAdminOrder(order.code)
      if (expandedCode.value !== order.code) return
      expandedDetail.value = loaded
      adminMemoDraft.value = loaded.adminMemo || ''
    } catch (error) {
      if (expandedCode.value === order.code) {
        expandedError.value = error instanceof Error ? error.message : '주문을 불러오지 못했습니다.'
      }
    } finally {
      if (expandedCode.value === order.code) expandedLoading.value = false
    }
  }
  if (!opts?.scroll) return
  await nextTick()
  document.getElementById(`aord-detail-${order.code}`)?.scrollIntoView({
    block: 'center',
    behavior: 'smooth',
  })
}

async function toggleExpand(order: AdminMallOrder) {
  if (expandedCode.value === order.code) {
    closeExpand()
    return
  }
  await expandOrder(order)
}

function closeExpand() {
  expandedCode.value = ''
  expandedDetail.value = null
  expandedError.value = ''
}

async function refreshExpanded() {
  if (!expandedCode.value) return
  const code = expandedCode.value
  try {
    const loaded = await fetchAdminOrder(code)
    if (expandedCode.value !== code) return
    expandedDetail.value = loaded
    adminMemoDraft.value = loaded.adminMemo || ''
  } catch {
    // 조용히 실패 (목록은 이미 reload됨)
  }
}

async function saveMemo() {
  const active = expandedDetail.value
  if (!active || memoBusy.value) return
  if (!window.confirm('내부 메모를 저장할까요?')) return
  memoBusy.value = true
  try {
    const result = await saveAdminOrderMemo(active.code, adminMemoDraft.value)
    if (expandedDetail.value?.code === active.code) expandedDetail.value = result.order
    adminMemoDraft.value = result.order.adminMemo || ''
    notify('내부 메모를 저장했습니다.')
  } catch (error) {
    notify(error instanceof Error ? error.message : '메모를 저장하지 못했습니다.')
  } finally {
    memoBusy.value = false
  }
}

const expandedPlatformMargin = computed(() =>
  (expandedDetail.value?.items ?? []).reduce((sum, item) => sum + item.platformMargin, 0),
)

const expandedRefund = computed(() => {
  const order = expandedDetail.value
  const totals = mallOrderRefundTotals(order?.items ?? [])
  const charged = Math.max(0, Number(order?.totalAmount) || 0)
  return {
    ...totals,
    balance: Math.max(0, charged - totals.refunded),
  }
})

// 확장된 주문의 상품을 판매업체별로 그룹핑
const expandedSellerGroups = computed(() => {
  const items = expandedDetail.value?.items ?? []
  const map = new Map<string, typeof items>()
  for (const item of items) {
    const key = item.storeName || '판매업체 미지정'
    const arr = map.get(key) ?? []
    arr.push(item)
    map.set(key, arr)
  }
  return Array.from(map.entries()).map(([storeName, storeItems]) => {
    const amount = storeItems.reduce((sum, item) => sum + item.amount, 0)
    return { storeName, items: storeItems, amount }
  })
})

async function onConfirm(order: AdminMallOrder) {
  if (working.value) return

  const ok = await confirmDialog({
    title: '입금 확인',
    message: '통장에서 입금을 확인하셨나요? 확인하면 판매업체에 주문이 전달되고 안내 메일·문자가 갑니다.',
    detail: [
      order.code,
      order.orgName || order.depositor || '',
      `입금액 ${formatWon(order.totalAmount)}원`,
      order.depositor ? `입금자 ${order.depositor}` : '',
    ]
      .filter(Boolean)
      .join('\n'),
    confirmLabel: '입금 확인',
    tone: 'primary',
  })
  if (!ok) return

  working.value = order.code

  try {
    const { message } = await confirmOrderPayment(order.code)
    notify(message || '결제확인 처리했습니다.')
    await reload()
    if (expandedCode.value === order.code) await refreshExpanded()
  } catch (error) {
    notify(error instanceof Error ? error.message : '결제확인에 실패했습니다.')
  } finally {
    working.value = ''
  }
}

async function onUnconfirm(order: AdminMallOrder) {
  if (working.value) return

  const ok = await confirmDialog({
    title: '결제확인 취소',
    message:
      '주문을 끝내는 것이 아닙니다. 결제확인만 취소하고 입금 대기로 되돌립니다. 나중에 다시 결제확인할 수 있습니다. 판매업체 목록에서 주문이 사라지고 재고가 복구됩니다. 주문을 아예 끝내려면 「주문 취소」를 누르세요.',
    detail: [
      order.code,
      order.orgName || order.depositor || '',
      `입금액 ${formatWon(order.totalAmount)}원`,
    ]
      .filter(Boolean)
      .join('\n'),
    confirmLabel: '입금대기로 되돌리기',
    tone: 'danger',
  })
  if (!ok) return

  working.value = order.code

  try {
    const { message } = await unconfirmOrderPayment(order.code)
    notify(message || '결제확인을 취소했습니다.')
    await reload()
    if (expandedCode.value === order.code) await refreshExpanded()
  } catch (error) {
    notify(error instanceof Error ? error.message : '결제확인 취소에 실패했습니다.')
  } finally {
    working.value = ''
  }
}

function onCancel(order: AdminMallOrder) {
  cancelTarget.value = order
  cancelReason.value = ''
}

function closeCancel() {
  cancelTarget.value = null
  cancelReason.value = ''
}

async function submitCancel() {
  if (!cancelTarget.value || !cancelReason.value || working.value) return
  const ok = await confirmDialog({
    title: '주문 취소',
    message:
      cancelTarget.value.payMethod === 'TOSS' && cancelTarget.value.status === 'PAID'
        ? '이 주문을 끝냅니다. 다시 살릴 수 없습니다. 결제된 금액은 카드·간편결제로 환불됩니다.'
        : '이 주문을 끝냅니다. 다시 살릴 수 없습니다. 입금 확인만 되돌리려면 창을 닫고 「결제확인 취소」를 누르세요.',
    detail: `${cancelTarget.value.code}\n사유: ${cancelReason.value}`,
    confirmLabel: '주문 취소 확정',
    tone: 'danger',
  })
  if (!ok) return

  working.value = cancelTarget.value.code
  const canceledCode = cancelTarget.value.code
  try {
    await cancelOrderByAdmin(cancelTarget.value.code, cancelReason.value)
    notify('주문을 취소했습니다.')
    closeCancel()
    await reload()
    if (expandedCode.value === canceledCode) await refreshExpanded()
  } catch (error) {
    notify(error instanceof Error ? error.message : '주문을 취소하지 못했습니다.')
  } finally {
    working.value = ''
  }
}

const API_ORIGIN = (process.env.VUE_APP_API_ORIGIN || '').replace(/\/$/, '')

function taxStatusLabel(status?: string) {
  return MALL_TAX_STATUS_LABEL[String(status || '').toUpperCase()] || '미요청'
}

function bizCertHref(path: string) {
  return path.startsWith('http') ? path : `${API_ORIGIN}${path}`
}

function taxDocHref(path: string) {
  return path.startsWith('http') ? path : `${API_ORIGIN}${path}`
}

function formatDateTime(value: string | null): string {
  if (!value) return '-'
  const date = new Date(value)
  return `${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')} ${String(
    date.getHours(),
  ).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

function formatOrgDate(value: string | null | undefined): string {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`
}

async function openOrgDetail(memberCode: string, fallbackName = '', orderCode = '') {
  const code = String(memberCode || '').trim()
  if (!code) return
  orgModalOpen.value = true
  orgLoading.value = true
  orgError.value = ''
  orgFocusOrderCode.value = String(orderCode || '').trim()
  orgDetail.value = fallbackName
    ? ({
        orgName: fallbackName,
        mCode: code,
        userId: '',
        bossName: '',
        bossPhone: '',
        bossEmail: '',
        discountPct: 0,
        taxRequests: [],
      } as AdminMemberRow)
    : null
  try {
    orgDetail.value = await fetchAdminMember(code)
  } catch (error: any) {
    orgError.value = error?.message || '회원 정보를 불러오지 못했습니다.'
  } finally {
    orgLoading.value = false
  }
}

function closeOrgDetail() {
  orgModalOpen.value = false
  orgLoading.value = false
  orgError.value = ''
  orgDetail.value = null
  orgFocusOrderCode.value = ''
}

function memberTaxTone(status?: string) {
  const key = String(status || '').toUpperCase()
  if (key === 'DONE') return 'sui-badge--green'
  if (key === 'REJECTED') return 'sui-badge--red'
  if (key === 'REQUESTED') return 'sui-badge--amber'
  return 'sui-badge--gray'
}
</script>

<style lang="scss" src="./AdminOrdersPage.scss"></style>
